<template>
  <div class="als">
    <div v-if="loading" class="als-loading"><div class="als-spinner"></div></div>
    <div v-if="loadError && !loading" class="als-error">
      <p class="als-error-title">Could not load alert settings</p>
      <p class="als-error-msg">{{ loadError }}</p>
      <button class="als-btn als-btn-outline" type="button" @click="fetchAll">Retry</button>
    </div>

    <template v-if="!loading && !loadError">
      <div class="als-page-head">
        <div class="als-page-head-text">
          <h1 class="als-title">Alert Settings</h1>
          <p class="als-subtitle">Choose which alerts you receive and how often.</p>
        </div>
      </div>

      <div class="als-hint-banner" role="note">
        <svg class="als-hint-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
        <div class="als-hint-body"><strong>Real-time</strong> alerts deliver the moment Amazon broadcasts the change. <strong>Daily</strong> bundles changes into one morning email. <strong>Weekly</strong> rolls them into your Monday digest.</div>
      </div>

      <div v-if="showInboxMode" class="als-config-card als-inbox-card">
        <div class="als-config-top">
          <div>
            <h3 class="als-config-title">Inbox mode</h3>
            <p class="als-config-hint">How alert emails land in your inbox.<template v-if="!canEditThreadedAlerts"> Only the account owner or an admin can change this.</template></p>
          </div>
        </div>
        <div class="als-inbox-options" role="radiogroup" aria-label="Inbox mode">
          <label
            v-for="opt in inboxModeOptions"
            :key="opt.value"
            class="als-inbox-option"
            :class="{ 'als-inbox-option-sel': threadedAlerts === opt.value, 'als-toggle-busy': inboxModeSaving }"
          >
            <input
              type="radio"
              name="als-inbox-mode"
              :checked="threadedAlerts === opt.value"
              :disabled="inboxModeSaving || !canEditThreadedAlerts"
              @change="onInboxModeChange(opt.value)"
            >
            <span class="als-inbox-option-text">
              <strong>{{ opt.label }}</strong>
              <span>{{ opt.hint }}</span>
            </span>
          </label>
        </div>
      </div>

      <div class="als-config-card als-digest-card">
        <div class="als-config-top">
          <div>
            <h3 class="als-config-title">Offer activity digest</h3>
            <p class="als-config-hint">When on, Buy Box losses to Amazon Retail or authorised resellers batch every 15 minutes instead of sending immediately. Ignore still never emails.</p>
          </div>
          <label class="als-toggle" :class="{ 'als-toggle-busy': digestSaving }">
            <input
              type="checkbox"
              :checked="offerActivityDigest"
              :disabled="digestSaving"
              @change="onDigestToggle($event.target.checked)"
            >
            <span class="als-toggle-slider"></span>
          </label>
        </div>
      </div>

      <div class="als-config-card">
        <div class="als-config-top">
          <div>
            <h3 class="als-config-title">Configuring alerts for</h3>
            <p class="als-config-hint">{{ isOwner ? 'Admin only · Members receive alerts based on this configuration' : 'Your personal alert preferences' }}</p>
          </div>
          <select
            v-if="dropdownUsers.length"
            v-model="selectedUserEmail"
            class="als-user-select"
            :disabled="!isOwner"
          >
            <option v-for="u in dropdownUsers" :key="u.email" :value="u.email">{{ formatUserLabel(u) }}</option>
          </select>
        </div>

        <div class="als-brands">
          <div v-if="hasMultiMp" class="als-mp-default-head">
            <span class="als-mp-default-title">Default marketplaces</span>
            <span class="als-mp-default-hint">Every alert sends for these unless you give it its own marketplaces below.</span>
          </div>
          <div v-for="br in assignedStorefronts" :key="br.key" class="als-brand-row">
            <span class="als-brand-name">{{ br.name }}</span>
            <div v-if="sfMpCodes(br).length > 1" class="als-mp-chips" role="group" :aria-label="br.name + ' default marketplaces'">
              <button
                type="button"
                :class="['als-mp-chip', 'als-mp-chip-all', { active: mpIsAll(defaultMpScope, br) }]"
                :aria-pressed="mpIsAll(defaultMpScope, br) ? 'true' : 'false'"
                :disabled="!mpDefaultEditable"
                @click="setMpAll(defaultMpScope, br)"
              >All</button>
              <button
                v-for="mp in sfMpCodes(br)"
                :key="'dmp-' + br.key + '-' + mp.code"
                type="button"
                :class="['als-mp-chip', { active: mpChipOn(defaultMpScope, br, mp.code) }]"
                :aria-pressed="mpChipOn(defaultMpScope, br, mp.code) ? 'true' : 'false'"
                :disabled="!mpDefaultEditable"
                @click="toggleMpChip(defaultMpScope, br, mp.code)"
              ><i :class="mpFlagClass(mp.raw) + ' flag'"></i>{{ mpLabel(mp.raw) }}</button>
            </div>
            <div v-else class="als-mp-tags">
              <span v-for="code in br.mpCodes" :key="code" class="als-mp-tag">
                <i :class="mpFlagClass(code) + ' flag'"></i>{{ mpLabel(code) }}
              </span>
            </div>
            <p v-if="mpStale(defaultMpScope, br)" class="als-mp-warn">No active marketplace selected for {{ br.name }}. Alerts that follow your default send nothing for it.</p>
          </div>
          <p v-if="!assignedStorefronts.length" class="als-muted-sm">No storefronts assigned.</p>
        </div>

        <p class="als-config-footer">
          Assignment managed in <router-link to="/settings/account" class="als-link">Account &amp; Team</router-link>.
          Narrow any alert to specific storefronts using the Storefronts selector on each alert.
          <template v-if="hasMultiMp">Products in muted marketplaces are still tracked and stay on your dashboard.</template>
        </p>
      </div>

      <div class="als-config-card als-locale-card">
        <div class="als-config-top">
          <div>
            <h3 class="als-config-title">Notification language</h3>
            <p class="als-config-hint">Email labels and copy only — your listing titles and Amazon field values are never translated. Saves automatically when you change the selection.</p>
          </div>
          <select
            v-model="notificationLocale"
            class="als-user-select"
            :disabled="localeSaving || (!isOwner && selectedUserEmail !== loginEmail)"
            @change="saveNotificationLocale"
          >
            <option v-for="opt in notificationLocaleOptions" :key="opt.code" :value="opt.code">{{ opt.label }}</option>
          </select>
        </div>
        <p v-if="localeSaving" class="als-muted-sm">Saving language…</p>
        <p v-else-if="localeError" class="als-error-msg als-locale-error">{{ localeError }}</p>
        <p v-else-if="localeSaved" class="als-muted-sm als-locale-saved">Language saved.</p>
      </div>

      <div class="als-panel">
        <div v-for="cat in categorizedRows" :key="'cat-' + cat.name">
          <div class="als-cat">{{ cat.name }}</div>
          <div v-for="row in cat.rows" :key="row.id" class="als-row-wrap">
            <div class="als-row" :class="{ 'als-row-locked': rowLocked(row) }">
              <div class="als-row-main">
                <div class="als-alert-title">
                  {{ row.title }}
                  <span v-if="mpPillVisible(row)" :class="['als-mp-pill', { 'als-mp-pill-own': rowMpOwn(row) }]">{{ mpPillText(row) }}</span>
                </div>
                <div class="als-alert-desc">
                  <span class="als-includes-label">Includes:</span>
                  {{ row.includes }}
                  <span v-if="row.note" class="als-note">{{ row.note }}</span>
                </div>
              </div>
              <div class="als-row-controls">
                <div class="als-row-controls-stack">
                  <div class="als-row-controls-top">
                    <span v-if="row.alwaysOn" class="als-pill-on">Always on</span>
                    <span v-else-if="rowLocked(row)" class="als-starter-lock als-starter-lock--toggle">
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2h1a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6a2 2 0 012-2h1zm2 0h6V7a3 3 0 10-6 0v2z" clip-rule="evenodd"/></svg>
                      Starter+
                    </span>
                    <template v-else>
                      <div v-if="showStorefrontChip(row)" class="als-sf-chip" @click.stop>
                        <button
                          type="button"
                          class="als-sf-chip-btn"
                          :title="storefrontChipTooltip(row)"
                          :aria-expanded="openStorefrontRowId === row.id ? 'true' : 'false'"
                          @click="toggleStorefrontMenu(row)"
                        >{{ storefrontChipLabel(row) }} <span class="als-sf-caret">▾</span></button>
                        <div v-if="openStorefrontRowId === row.id" class="als-sf-menu" role="dialog" aria-label="Storefronts for this alert">
                          <label class="als-sf-opt">
                            <input type="radio" :name="'sf-mode-' + row.id" :checked="!rowSellerIdsFor(row).length" @change="setStorefrontsAll(row)">
                            All storefronts
                          </label>
                          <label class="als-sf-opt">
                            <input type="radio" :name="'sf-mode-' + row.id" :checked="rowSellerIdsFor(row).length > 0" @change="setStorefrontsOnly(row)">
                            Only selected:
                          </label>
                          <div class="als-sf-list">
                            <label v-for="sf in assignedStorefronts" :key="'sf-' + row.id + '-' + sf.key" class="als-sf-opt">
                              <input
                                type="checkbox"
                                :checked="storefrontSelected(row, sf.id)"
                                @change="onStorefrontCheck(row, sf.id, $event.target.checked, $event)"
                              >
                              <span class="als-sf-name">{{ sf.name }}</span>
                              <span class="als-sf-flags">
                                <i v-for="code in sf.mpCodes" :key="'f-' + sf.key + '-' + code" :class="mpFlagClass(code) + ' flag'" :title="mpLabel(code)"></i>
                              </span>
                            </label>
                          </div>
                          <p v-if="storefrontMenuError[row.id]" class="als-sf-err">{{ storefrontMenuError[row.id] }}</p>
                          <p class="als-sf-note">“All” also covers storefronts you add later.</p>
                        </div>
                      </div>
                      <label class="als-toggle">
                        <input type="checkbox" :checked="rowOn(row)" @change="onRowToggle(row, $event.target.checked)">
                        <span class="als-toggle-slider"></span>
                      </label>
                    </template>
                  </div>
                  <div
                    v-if="row.delayMinutesConfigurable && rowOn(row) && !rowLocked(row)"
                    class="als-row-controls-bottom als-delay-inline"
                  >
                    <div class="als-seg" role="group" aria-label="Buy Box alert delay">
                      <button
                        type="button"
                        :class="['als-seg-btn', { active: !isBuyboxDelayCustom(row) }]"
                        @click="setBuyboxDelayImmediate(row)"
                      >Real-time</button>
                      <button
                        type="button"
                        :class="['als-seg-btn', { active: isBuyboxDelayCustom(row) }]"
                        @click="enableBuyboxDelayInput(row)"
                      >Custom</button>
                    </div>
                    <template v-if="isBuyboxDelayCustom(row)">
                      <input
                        :class="['als-delay-input', { 'als-delay-input-err': !!delayErrorForRow(row) }]"
                        type="number"
                        min="20"
                        max="40"
                        step="1"
                        :value="delayDraftForRow(row)"
                        @input="onBuyboxDelayInput(row, $event.target.value)"
                      >
                      <span class="als-delay-unit">min</span>
                    </template>
                  </div>
                  <div
                    v-else-if="!row.delayMinutesConfigurable && (row.alwaysOn || row.digestFixed || deliveryRealtimeBadgeOnly(row) || (!rowLocked(row) && !row.alwaysOn && !deliveryRealtimeBadgeOnly(row)))"
                    class="als-row-controls-bottom"
                  >
                    <template v-if="row.alwaysOn">
                      <span class="als-badge-freq">{{ row.digestFixed ? 'Digest' : 'Real-time' }}</span>
                    </template>
                    <template v-else-if="row.digestFixed">
                      <span class="als-badge-freq">Digest</span>
                    </template>
                    <template v-else-if="deliveryRealtimeBadgeOnly(row)">
                      <span class="als-badge-freq">{{ row.fixedBadge || 'Real-time' }}</span>
                    </template>
                    <template v-else>
                      <div class="als-seg" role="group">
                        <button
                          v-for="opt in deliveryOptionsForRow(row)"
                          :key="opt"
                          type="button"
                          :class="['als-seg-btn', { active: deliveryFor(row) === opt }]"
                          @click="setDelivery(row, opt)"
                        >{{ opt === 'realtime' ? 'Real-time' : (opt === 'daily' ? 'Daily' : 'Weekly') }}</button>
                      </div>
                    </template>
                  </div>
                </div>
                <p
                  v-if="row.delayMinutesConfigurable && rowOn(row) && !rowLocked(row) && delayErrorForRow(row)"
                  class="als-delay-error-inline"
                >{{ delayErrorForRow(row) }}</p>
                <p v-if="storefrontSelectionStale(row)" class="als-sf-warn">No assigned storefronts selected — this alert won't send.</p>
              </div>
            </div>
            <details
              v-if="subtypeBoxVisible(row)"
              class="als-bf als-st"
              :open="!!subtypeBoxOpen[row.id]"
              @toggle="$set(subtypeBoxOpen, row.id, $event.target.open)"
            >
              <summary class="als-bf-head">
                <span class="als-bf-caret">▸</span>Alert me about
                <span class="als-bf-sub">— {{ subtypeSummary(row) }}</span>
              </summary>
              <div class="als-st-grid" role="group" :aria-label="row.title + ' types'">
                <label v-for="opt in subtypeOptions(row)" :key="'st-' + row.id + '-' + opt.key" class="als-st-opt">
                  <input
                    type="checkbox"
                    :checked="subtypeOn(row, opt.key)"
                    @change="onSubtypeCheck(row, opt.key, $event.target.checked, $event)"
                  >
                  {{ opt.label }}
                </label>
              </div>
              <p v-if="subtypeError[row.id]" class="als-mp-warn">{{ subtypeError[row.id] }}</p>
              <p class="als-bf-ro">
                <button v-if="mutedSubtypesFor(row).length" type="button" class="als-st-reset" @click="setAllSubtypes(row)">Turn all back on</button>
                New types Amazon adds later are on automatically.
              </p>
            </details>
            <details
              v-if="row.settingsKey === 'review_health' && rowOn(row) && !rowLocked(row)"
              class="als-bf als-st"
              :open="!!settingsBoxOpen[row.id]"
              @toggle="$set(settingsBoxOpen, row.id, $event.target.open)"
            >
              <summary class="als-bf-head">
                <span class="als-bf-caret">▸</span>Sensitivity
                <span class="als-bf-sub">— {{ reviewSensitivitySummary(row) }}</span>
              </summary>
              <p class="als-settings-label">Star rating drop</p>
              <div class="als-st-grid" role="radiogroup" :aria-label="row.title + ' rating sensitivity'">
                <label class="als-st-opt">
                  <input
                    type="radio"
                    :name="'rs-' + row.id"
                    :checked="rowSettingsFor(row).rating_sensitivity === 'half_star'"
                    @change="setRowSetting(row, 'rating_sensitivity', 'half_star')"
                  >
                  Every half-star the customer sees (default)
                </label>
                <label class="als-st-opt">
                  <input
                    type="radio"
                    :name="'rs-' + row.id"
                    :checked="rowSettingsFor(row).rating_sensitivity === 'tenth'"
                    @change="setRowSetting(row, 'rating_sensitivity', 'tenth')"
                  >
                  Every 0.1 drop
                </label>
              </div>
              <div class="als-settings-line">
                <span class="als-subrow-label">Minimum rating-count drop</span>
                <input
                  class="als-delay-input"
                  type="number"
                  min="1"
                  step="1"
                  :value="rowSettingsFor(row).min_rating_count_drop"
                  @change="onMinRatingCountDropChange(row, $event)"
                >
              </div>
            </details>
            <p v-if="hasMultiMp && (row.alwaysOn || (row.digestFixed && rowOn(row) && !rowLocked(row)))" class="als-mp-follow als-mp-follow-row">
              Marketplaces: follows your default (<b>{{ mpScopeSummary(defaultMpScope, mpStorefrontsAll) }}</b>). {{ row.alwaysOn ? 'Always-on alerts can\'t have their own set.' : 'This digest can\'t have its own set.' }}
            </p>
            <details
              v-if="mpBoxVisible(row)"
              class="als-bf als-mp"
              :open="!!mpBoxOpen[row.id]"
              @toggle="$set(mpBoxOpen, row.id, $event.target.open)"
            >
              <summary class="als-bf-head">
                <span class="als-bf-caret">▸</span>Marketplaces
                <span class="als-bf-sub">— {{ rowMpOwn(row) ? 'own set' : 'follows default' }}</span>
              </summary>
              <p class="als-bf-hint">Which marketplaces send this alert. Use your default, or pick a different set just for this alert.</p>
              <div class="als-seg als-mp-mode" role="group" :aria-label="row.title + ' marketplace mode'">
                <button type="button" :class="['als-seg-btn', { active: !rowMpOwn(row) }]" @click="setRowMpMode(row, false)">Use default</button>
                <button type="button" :class="['als-seg-btn', { active: rowMpOwn(row) }]" @click="setRowMpMode(row, true)">Own marketplaces</button>
              </div>
              <template v-if="rowMpOwn(row)">
                <div v-for="sf in mpStorefronts(row)" :key="'mp-' + row.id + '-' + sf.key" class="als-bf-line">
                  <span v-if="mpStorefronts(row).length > 1" class="als-bf-name">{{ sf.name }}</span>
                  <div class="als-mp-chips" role="group" :aria-label="sf.name + ' marketplaces for ' + row.title">
                    <button
                      type="button"
                      :class="['als-mp-chip', 'als-mp-chip-all', { active: mpIsAll(rowMpScope[row.id], sf) }]"
                      :aria-pressed="mpIsAll(rowMpScope[row.id], sf) ? 'true' : 'false'"
                      @click="setMpAll(rowMpScope[row.id], sf)"
                    >All</button>
                    <button
                      v-for="mp in sfMpCodes(sf)"
                      :key="'rmp-' + row.id + '-' + sf.key + '-' + mp.code"
                      type="button"
                      :class="['als-mp-chip', { active: mpChipOn(rowMpScope[row.id], sf, mp.code) }]"
                      :aria-pressed="mpChipOn(rowMpScope[row.id], sf, mp.code) ? 'true' : 'false'"
                      @click="toggleMpChip(rowMpScope[row.id], sf, mp.code)"
                    ><i :class="mpFlagClass(mp.raw) + ' flag'"></i>{{ mpLabel(mp.raw) }}</button>
                  </div>
                  <p v-if="mpStale(rowMpScope[row.id], sf)" class="als-mp-warn">No active marketplace selected for {{ sf.name }}. This alert sends nothing for it.</p>
                </div>
              </template>
              <p v-else class="als-mp-follow">Following default: <b>{{ mpScopeSummary(defaultMpScope, mpStorefronts(row)) }}</b></p>
              <p class="als-bf-ro">Applies only to {{ selectedUserEmail === loginEmail ? 'your' : selectedUserEmail + '’s' }} emails</p>
            </details>
            <details
              v-if="row.brandFilter && rowOn(row) && !rowLocked(row) && brandStorefronts(row).length"
              class="als-bf"
              :open="brandFilterIsOpen(row.brandFilter)"
              @toggle="setBrandFilterOpen(row.brandFilter, $event.target.open)"
            >
              <summary class="als-bf-head">
                <span class="als-bf-caret">▸</span>Brand filter
                <span class="als-bf-sub">— optional{{ brandFilterSummary(row) }}</span>
              </summary>
              <p class="als-bf-hint">{{ row.brandFilterHint }} Leave empty to be alerted on every listing.</p>
              <div v-for="sf in brandStorefronts(row)" :key="'bf-' + row.brandFilter + '-' + sf.key" class="als-bf-line">
                <span v-if="brandStorefronts(row).length > 1" class="als-bf-name">{{ sf.name }}</span>
                <span v-if="!brandsFor(row.brandFilter, sf.id).length" class="als-bf-all">All brands</span>
                <span v-for="(b, bi) in brandsFor(row.brandFilter, sf.id)" :key="'bp-' + row.brandFilter + '-' + sf.key + '-' + bi" class="als-bf-pill">
                  {{ b }}
                  <button v-if="brandFilterEditable" type="button" class="als-bf-x" :aria-label="'Remove ' + b" @click="removeBrand(row.brandFilter, sf.id, bi)">×</button>
                </span>
                <div v-if="brandFilterEditable" class="als-bf-add" @click.stop>
                  <button type="button" class="als-bf-add-btn" @click="toggleBrandPicker(row.brandFilter, sf.id, row.id)">+ Add brand ▾</button>
                  <div v-if="brandPickerKey === brandPickerKeyFor(row.id, sf.id)" class="als-bf-pick">
                    <input
                      class="als-bf-search"
                      type="text"
                      placeholder="Search or type a brand"
                      :value="brandQuery"
                      @input="onBrandQuery(sf.id, $event.target.value)"
                      @keydown.enter.prevent="addTypedBrand(row.brandFilter, sf.id)"
                    >
                    <p v-if="brandOptionsLoading" class="als-bf-empty">Loading brands…</p>
                    <template v-else>
                      <button
                        v-for="opt in brandPickerOptions(row.brandFilter, sf.id)"
                        :key="'bo-' + opt.brand"
                        type="button"
                        class="als-bf-opt"
                        @click="addBrand(row.brandFilter, sf.id, opt.brand)"
                      ><span>{{ opt.brand }}</span><small>{{ opt.asin_count }} ASIN{{ opt.asin_count === 1 ? '' : 's' }}</small></button>
                      <button
                        v-if="brandQueryIsCustom(row.brandFilter, sf.id)"
                        type="button"
                        class="als-bf-opt als-bf-custom"
                        @click="addTypedBrand(row.brandFilter, sf.id)"
                      >Use “{{ brandQuery.trim() }}”</button>
                      <p v-if="!brandPickerOptions(row.brandFilter, sf.id).length && !brandQueryIsCustom(row.brandFilter, sf.id)" class="als-bf-empty">No more brands in this catalog</p>
                    </template>
                  </div>
                </div>
              </div>
              <p class="als-bf-ro">Applies only to {{ selectedUserEmail === loginEmail ? 'your' : selectedUserEmail + '’s' }} emails{{ brandFilterEditable ? '' : ' · Only the account owner or an admin can edit another user’s filter' }}</p>
            </details>
            <div
              v-if="!row.alwaysOn && !deliveryRealtimeBadgeOnly(row) && (row.digestFixed || deliveryFor(row) === 'daily') && rowOn(row) && !rowLocked(row)"
              class="als-subrow"
            >
              <span class="als-subrow-label">Send at</span>
              <select
                class="als-time-select"
                :value="digestHourForRow(row.id)"
                @change="setDigestHourForRow(row.id, Number($event.target.value))"
              >
                <option v-for="h in digestHourOptions" :key="h" :value="h">{{ formatDigestHour(h) }}</option>
              </select>
              <span class="als-subrow-hint">{{ row.digestFixed ? 'Browser local time' : 'Browser local time · consolidated with other daily alerts' }}</span>
            </div>
            <div
              v-if="!row.alwaysOn && !row.digestFixed && !deliveryRealtimeBadgeOnly(row) && deliveryFor(row) === 'weekly' && rowOn(row) && !rowLocked(row)"
              class="als-subrow"
            >
              <span class="als-subrow-label">Every</span>
              <select
                class="als-time-select"
                :value="weeklyDay[row.id] || weeklyScheduleDayDefault"
                @change="setWeeklyDay(row.id, $event.target.value)"
              >
                <option v-for="d in weekDaysList" :key="d.api" :value="d.api">{{ d.label }}</option>
              </select>
              <span class="als-subrow-label">at</span>
              <select
                class="als-time-select"
                :value="digestHourForRow(row.id)"
                @change="setDigestHourForRow(row.id, Number($event.target.value))"
              >
                <option v-for="h in digestHourOptions" :key="'wh-' + h" :value="h">{{ formatDigestHour(h) }}</option>
              </select>
              <span class="als-subrow-hint">Browser local time · consolidated with other weekly alerts</span>
            </div>
          </div>
        </div>
      </div>

      <div class="als-footer-bar">
        <button
          class="als-btn als-btn-primary"
          type="button"
          :disabled="!hasChanges || saving || hasDelayValidationError"
          :class="{ 'als-btn-loading': saving }"
          @click="save"
        >Save Preferences</button>
      </div>

      <transition name="als-slide-up">
        <div v-if="successMsg" class="als-toast als-toast-ok">{{ successMsg }}</div>
      </transition>
      <transition name="als-slide-up">
        <div v-if="errorMsg" class="als-toast als-toast-err">{{ errorMsg }}</div>
      </transition>
    </template>
  </div>
</template>

<script>
var MP_LABELS = { us: 'US', gb: 'UK', uk: 'UK', ca: 'CA', de: 'DE', fr: 'FR', it: 'IT', es: 'ES', jp: 'JP', au: 'AU', 'in': 'IN', mx: 'MX', br: 'BR', nl: 'NL', sa: 'SA', ae: 'AE', sg: 'SG', se: 'SE', pl: 'PL', be: 'BE', at: 'AT', za: 'ZA', tr: 'TR', eg: 'EG' }
var REGION_MARKETPLACE = { NA: 'us', EU: 'gb', IN: 'in', ME: 'ae', FE: 'jp' }

/** alertCategory values must match GET/PUT /api/v1/notification-preferences */
var ROWS = [
  { id: 'buybox_lost', alertCategory: 'buybox_lost', category: 'Buy Box & Pricing', title: 'Buy Box Issues', includes: 'Buy Box state, winning seller ID, listing price, shipping cost, fulfillment type (FBA/FBM), seller feedback rating, Prime status.', wdt: 'LOST_BUYBOX', deliveryRealtimeBadgeOnly: true, delayMinutesConfigurable: true, brandFilter: 'buybox_lost', brandFilterHint: 'Only alert when the Buy Box is lost on listings of these brands.' },
  { id: 'no_featured_offer', alertCategory: 'no_featured_offer', category: 'Buy Box & Pricing', title: 'No Featured Offer (nobody holds the Buy Box)', includes: 'ASIN, SKU, since when, likely cause, and whether it is still unowned or back to you. Real-time re-checks the listing after about 25 minutes before emailing; Daily lists them in one morning email.', note: 'Separate from Buy Box Issues: turning this off does not stop Buy Box lost alerts.', localKey: 'NO_FEATURED_OFFER', deliveryOptions: ['realtime', 'daily'], brandFilter: 'no_featured_offer', brandFilterHint: 'Only alert when nobody holds the Buy Box on listings of these brands.' },
  { id: 'competitor_price', alertCategory: 'listing_price_change', category: 'Buy Box & Pricing', title: 'Price Change', includes: 'ASIN, seller ID, old price, new price, % change, fulfillment type, Buy Box impact.', wdt: 'PRICE', deliveryRealtimeBadgeOnly: true, brandFilter: 'price_change', brandFilterHint: 'Only alert when your price changes on listings of these brands.' },
  { id: 'competitor_price_change', alertCategory: 'competitor_price_change', category: 'Buy Box & Pricing', title: 'Competitor Price Change', includes: 'ASIN, competitor seller ID, old competitor price, new competitor price, % change, Buy Box impact.', localKey: 'COMPETITOR_PRICE_CHANGE', deliveryRealtimeBadgeOnly: true, brandFilter: 'competitor_price', brandFilterHint: 'Only alert when a competitor changes their price on listings of these brands.' },
  { id: 'competitive_price', alertCategory: 'competitive_price', category: 'Buy Box & Pricing', title: "Amazon competitive price (external price check)", includes: 'When your price + shipping goes above Amazon\'s competitive price, and when it comes back within it: ASIN, SKU, brand, competitive price old → new, your price + shipping per offer, gap, Buy Box holder, Manage Inventory link.', localKey: 'COMPETITIVE_PRICE', deliveryRealtimeBadgeOnly: true, tenantFlag: 'competitivePriceAlerts', brandFilter: 'competitive_price', brandFilterHint: 'Only alert (and include in the daily changes email) on listings of these brands. Separate from the Competitor Price Change list.' },
  { id: 'competitive_price_change', alertCategory: 'competitive_price_change', category: 'Buy Box & Pricing', title: 'Amazon competitive price changes (daily)', includes: 'Every change Amazon made to its competitive price in the last 24 hours: counts per brand, listings above it now, CSV of all changes.', localKey: 'COMPETITIVE_PRICE_CHANGE', digestFixed: true, tenantFlag: 'competitivePriceAlerts', brandFilter: 'competitive_price', brandFilterHint: 'Same brand list as Amazon competitive price (external price check). The daily email only includes listings of these brands.' },
  { id: 'price_floor', alertCategory: 'price_floor_breach', category: 'Buy Box & Pricing', title: 'Price Floor Breach', includes: 'ASIN, competitor price, your floor price, COGS, margin delta, recommended action.', note: 'Requires COGS to be set.', localKey: 'PRICE_FLOOR', alwaysOn: true, deliveryRealtimeBadgeOnly: true },
  { id: 'hijacker', alertCategory: 'hijacker', category: 'Competitive', title: 'Hijacker Alert', includes: 'ASIN, seller ID, business name, location, feedback rating, current price, fulfillment type, storefront link.', localKey: 'HIJACKER', defaultOn: true, deliveryRealtimeBadgeOnly: true, brandFilter: 'hijacker', brandFilterHint: 'Only alert when a new seller appears on listings of these brands.' },
  { id: 'suspended', alertCategory: 'listing_suppressed', category: 'Listing Health', title: 'Suspended Listings', includes: 'ASIN, suspension reason, suspension date, reinstatement steps (where Amazon provides them), and an all-clear email once Amazon stops reporting the issue.', localKey: 'LISTING_SUPPRESSED', defaultOn: true, deliveryRealtimeBadgeOnly: true, subtypes: 'listing_suppressed', brandFilter: 'listing_suppressed', brandFilterHint: 'Only alert when Amazon reports a listing issue on listings of these brands.' },
  { id: 'review_health', alertCategory: 'review_health', category: 'Listing Health', title: 'Review Health', includes: 'Star rating drops, rating count drops (reviews removed or split from a variation), and new critical reviews, per listing. Checked once a day from Amazon\'s review data, which runs 1–2 days behind. One email per storefront when something changes.', localKey: 'REVIEW_HEALTH', subtypes: 'review_health', deliveryRealtimeBadgeOnly: true, fixedBadge: 'Daily', settingsKey: 'review_health', brandFilter: 'review_health', brandFilterHint: 'Only alert when ratings or reviews change on listings of these brands.' },
  { id: 'account_health', alertCategory: 'account_health', category: 'Account Health', title: 'Account Health Changes', includes: 'Account status changes (Normal / At risk / Deactivated), Account Health Rating, and policy violation counts per category (food and product safety, authenticity, IP complaints, listing policy and more), checked every 6 hours. Amazon gives counts, not the ASIN; the email links to Account Health in Seller Central. With threaded emails, listing issues and their all-clears land in the same Account health conversation.', localKey: 'ACCOUNT_HEALTH', deliveryRealtimeBadgeOnly: true },
  { id: 'content_change', alertCategory: 'content_change', category: 'Product Content', title: 'Product Details Change', includes: 'ASIN, field changed (title / description / bullets / brand / A+ content), main image changes, category and parent ASIN / variation updates where applicable, before and after values, change timestamp.', wdt: 'PRODUCT_DETAILS_RECENT_CHANGE', subtypes: 'content_change', brandFilter: 'content_change', brandFilterHint: 'Only alert when product details change on listings of these brands. Includes adult classification.' },
  { id: 'attribute_change', alertCategory: 'attribute_change', category: 'Product Content', title: 'Backend Attribute Changes', includes: 'Changes to the attributes behind your listing (compliance, price, fulfilment, variations, identifiers, content, images), with before and after values. Larger changes come with a PDF.', localKey: 'ATTRIBUTE_CHANGE', deliveryRealtimeBadgeOnly: true, subtypes: 'attribute_change', noMarketplaceFilter: true, brandFilter: 'attribute_change', brandFilterHint: 'Only alert when backend attributes change on listings of these brands.' },
  { id: 'low_stock', alertCategory: 'stock_alert', category: 'Inventory', title: 'FBA & FBM Stock', includes: 'FBA: ASIN, fulfillable units, pending transshipment units, days of runway, threshold breached. FBM: SKU, available units, days of runway, threshold breached.', wdt: 'INVENTORY', brandFilter: 'stock_alert', brandFilterHint: 'Only alert on stock changes for listings of these brands. Shared with Stock Recovered and Stock Critical.' },
  { id: 'stock_recovered', alertCategory: 'stock_alert_recovered', category: 'Inventory', title: 'Stock Recovered (Amber → Green)', includes: 'ASIN, units recovered, previous warning threshold, current fulfillable units, days of runway restored.', wdt: 'INVENTORY', brandFilter: 'stock_alert', brandFilterHint: 'Same brand list as FBA & FBM Stock. Recoveries are sent only for listings of these brands.' },
  { id: 'stock_critical', alertCategory: 'stock_alert_critical', category: 'Inventory', title: 'Stock Critical (Amber → Red)', includes: 'ASIN, current fulfillable units, critical threshold breached, days of runway remaining, revenue at risk.', wdt: 'INVENTORY', brandFilter: 'stock_alert', brandFilterHint: 'Same brand list as FBA & FBM Stock. Critical stock is sent only for listings of these brands.' },
  { id: 'sales_drop', alertCategory: 'sales_volume_alert', category: 'Inventory', title: 'Sales Volume', includes: 'ASIN, current 7-day velocity, baseline velocity, % change, revenue delta.', wdt: 'SALES_DROP', brandFilter: 'sales_volume', brandFilterHint: 'Only alert when sales volume changes on listings of these brands.' },
  { id: 'fba_fee', alertCategory: 'fba_fee_change', category: 'Inventory', title: 'FBA Fee Change', includes: 'ASIN, fee type (referral / fulfillment / storage), old fee, new fee, margin impact.', localKey: 'FBA_FEE', brandFilter: 'fba_fee_change', brandFilterHint: 'Only alert when the FBA fee changes on listings of these brands.' },
  { id: 'digest_summary', alertCategory: 'daily_portfolio_digest', category: 'Digests', title: 'Daily Portfolio Digest', includes: 'Morning summary of your portfolio status.', localKey: 'DAILY_PORTFOLIO_DIGEST', digestFixed: true }
]

/** Legacy API category → current ROWS alertCategory (prefs load). */
var ALERT_CATEGORY_ALIASES = {
  daily_consolidated_digest: 'daily_portfolio_digest'
}

function rowForAlertCategory (alertCategory) {
  var cat = String(alertCategory || '')
  var mapped = ALERT_CATEGORY_ALIASES[cat] || cat
  return ROWS.filter(function (r) { return r.alertCategory === mapped })[0] || null
}

/** buybox_lost only: null = immediate; otherwise integer minutes in 20..40 (no clamping). */
function normalizeBuyboxDelayMinutes (raw) {
  if (raw == null || raw === '' || raw === 'immediate' || raw === 'null') return null
  var s = String(raw).trim()
  if (!/^\d+$/.test(s)) return null
  var n = parseInt(s, 10)
  if (n < 20 || n > 40) return null
  return n
}

/** Validate user-entered delay. Returns { value, error }. */
function validateBuyboxDelayMinutesInput (raw) {
  if (raw == null || String(raw).trim() === '') {
    return { value: null, error: 'Enter a value between 20 and 40 minutes' }
  }
  var s = String(raw).trim()
  if (!/^\d+$/.test(s)) {
    return { value: null, error: 'Enter a whole number between 20 and 40 minutes' }
  }
  var n = parseInt(s, 10)
  if (n < 20 || n > 40) {
    return { value: null, error: 'Delay must be between 20 and 40 minutes' }
  }
  return { value: n, error: null }
}

var NOTIFICATION_PREFS_URL = '/api/v1/notification-preferences'
var NOTIFICATION_PREFS_USERS_URL = '/api/v1/notification-preferences/users'
var NOTIFICATION_PREFS_LOCALE_URL = '/api/v1/notification-preferences/locale'
var NOTIFICATION_PREFS_MARKETPLACES_URL = '/api/v1/notification-preferences/marketplaces'

/** Account-level alerts are never filtered by marketplace (backend NO_MARKETPLACE_CATEGORIES). */
var NO_MARKETPLACE_CATEGORIES = ['amazon_connection', 'account_status']

/**
 * Types a user can switch off inside one alert (backend UserNotificationPreference::SUBTYPES), keyed by
 * row.subtypes. All on by default; the API stores the ones switched off (muted_subtypes).
 */
var ALERT_SUBTYPES = {
  content_change: [
    { key: 'title', label: 'Title' },
    { key: 'bullet_points', label: 'Bullet points' },
    { key: 'description', label: 'Description' },
    { key: 'brand', label: 'Brand' },
    { key: 'images', label: 'Images' },
    { key: 'category', label: 'Category' },
    { key: 'variations', label: 'Parent / variations' },
    { key: 'adult_classification', label: 'Adult classification' }
  ],
  listing_suppressed: [
    { key: 'suppression', label: 'Suppressed or hidden from search' },
    { key: 'compliance', label: 'Compliance issues (action required)' },
    { key: 'gating', label: 'Restricted (approval needed)' }
  ],
  review_health: [
    { key: 'rating_drop', label: 'Star rating drops' },
    { key: 'rating_count_drop', label: 'Rating count drops' },
    { key: 'critical_reviews', label: 'New critical reviews' }
  ],
  attribute_change: [
    { key: 'safety_and_compliance', label: 'Compliance & safety' },
    { key: 'offer', label: 'Price' },
    { key: 'shipping', label: 'Fulfilment' },
    { key: 'variations', label: 'Variations' },
    { key: 'product_identity', label: 'Identity & classification' },
    { key: 'product_details', label: 'Content' },
    { key: 'images', label: 'Images' }
  ]
}

/**
 * Per-alert `settings` object on a preference, keyed by row.settingsKey. Missing keys fall back to these
 * defaults (backend uses the same ones).
 */
var ROW_SETTINGS_DEFAULTS = {
  review_health: { rating_sensitivity: 'half_star', min_rating_count_drop: 1 }
}

/** Fill defaults and coerce types so load / save / dirty-compare all see the same shape. */
function normalizeRowSettings (settingsKey, raw) {
  var defaults = ROW_SETTINGS_DEFAULTS[settingsKey] || {}
  var src = raw && typeof raw === 'object' ? raw : {}
  var out = Object.assign({}, defaults)
  if (settingsKey === 'review_health') {
    out.rating_sensitivity = src.rating_sensitivity === 'tenth' ? 'tenth' : 'half_star'
    var n = parseInt(src.min_rating_count_drop, 10)
    out.min_rating_count_drop = !isNaN(n) && n >= 1 ? n : defaults.min_rating_count_drop
  }
  return out
}

/** API marketplace code: upper-case, GB stored as UK. */
function mpApiCode (code) {
  var c = String(code || '').toUpperCase()
  return c === 'GB' ? 'UK' : c
}

/**
 * Marketplace scope { sellerId: 'all' | [codes] } in compare/save form: "all" entries dropped (a missing
 * storefront means all), lists sorted. null stays null (= follow the default).
 */
function normalizeMpScope (scope) {
  if (scope == null) return null
  var out = {}
  Object.keys(scope).sort().forEach(function (k) {
    var v = scope[k]
    if (Array.isArray(v)) out[k] = v.slice().sort()
  })
  return out
}
var SELLERS_URL = '/api/v1/sellers'

function sellerBrandsUrl (sellerId) {
  return SELLERS_URL + '/' + encodeURIComponent(sellerId) + '/brands'
}

/** Brand-filterable alerts — must match Seller::BRAND_ALLOWLISTS keys on the backend. */
var BRAND_FILTER_ALERTS = ['hijacker', 'buybox_lost', 'price_change', 'competitor_price', 'competitive_price', 'no_featured_offer', 'listing_suppressed', 'content_change', 'attribute_change', 'stock_alert', 'sales_volume', 'fba_fee_change', 'review_health']

/** GET — one user's own brand lists on every storefront ({ user_id, lists: { sellerId: { alert: [] } } }). */
var USER_BRAND_ALLOWLISTS_URL = SELLERS_URL + '/brand-allowlists'

function sellerBrandAllowlistUrl (sellerId, alert) {
  return SELLERS_URL + '/' + encodeURIComponent(sellerId) + '/brand-allowlists/' + encodeURIComponent(alert)
}

/** { alert: {} } for every brand-filterable alert. */
function emptyBrandAllowlists () {
  var out = {}
  BRAND_FILTER_ALERTS.forEach(function (a) { out[a] = {} })
  return out
}

/** Display form — matches Seller.clean_brand on the backend: NFKC, zero-width dropped, any space collapsed. */
function cleanBrand (value) {
  var text = String(value == null ? '' : value)
  if (text.normalize) text = text.normalize('NFKC')
  return text.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '').replace(/\s+/g, ' ').trim()
}

/** Brand comparison key — matches Seller.normalize_brand on the backend (case/whitespace-insensitive). */
function brandKey (value) {
  return cleanBrand(value).toLowerCase()
}

/** Numeric seller id list from API `seller_ids` (ints or strings). */
function numericIdList (raw) {
  if (!Array.isArray(raw)) return []
  return raw.map(function (x) { return Number(x) }).filter(function (n) { return !isNaN(n) && n > 0 })
}

var NOTIFICATION_LOCALE_LABELS = {
  en: 'English',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  es: 'Spanish',
  pt: 'Portuguese',
  nl: 'Dutch',
  pl: 'Polish'
}

/** `delivery_schedule.weekday` JSON — full English weekday (backend expects these strings, not abbreviations like "thu"). */
var WEEKLY_SCHEDULE_DAY_DEFAULT = 'Monday'
var WEEKLY_SCHEDULE_DAYS = [
  { api: 'Monday', label: 'Mon' }, { api: 'Tuesday', label: 'Tue' }, { api: 'Wednesday', label: 'Wed' },
  { api: 'Thursday', label: 'Thu' }, { api: 'Friday', label: 'Fri' }, { api: 'Saturday', label: 'Sat' }, { api: 'Sunday', label: 'Sun' }
]
var WEEKDAY_TO_API_LOOKUP = (function () {
  var m = {}
  var legacyKeys = [['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']]
  legacyKeys.forEach(function (pair) {
    m[pair[0]] = pair[1]
    m[pair[1].toLowerCase()] = pair[1]
  })
  return m
})()

/** `user_account` row represents admin/owner for UI (matches Account & Team). */
function userAccountRowIsAdmin (ua) {
  if (!ua) return false
  if (ua.is_owner === true || ua.isOwner === true) return true
  if (ua.is_primary === true || ua.isPrimary === true) return true
  var r = String(ua.role != null ? ua.role : '').toLowerCase()
  return r === 'admin' || r === 'owner'
}

/** From GET /api/identity/settings `user_account[]`: `emailVerified` / `email_verified`. If absent, assume verified (legacy API). */
function userAccountEmailVerifiedFromSettings (ua) {
  if (!ua) return true
  var v = ua.emailVerified
  if (v === undefined || v === null) v = ua.email_verified
  if (v === undefined || v === null) return true
  return v === true || v === 'true' || v === 1 || v === '1'
}

/** Map API value, abbreviated UI, etc. → canonical weekday string matching WEEKLY_SCHEDULE_DAYS[].api */
function normalizeDeliveryWeekday (raw) {
  if (raw == null || String(raw).trim() === '') return WEEKLY_SCHEDULE_DAY_DEFAULT
  var s = String(raw).trim()
  var lc = s.toLowerCase()
  var hit = WEEKDAY_TO_API_LOOKUP[lc]
  return hit || WEEKLY_SCHEDULE_DAY_DEFAULT
}

function normalizeEmailKeyStatic (email) {
  return email ? String(email).trim().toLowerCase() : ''
}

/**
 * Deprecated IANA aliases some OS/browsers still return from
 * `Intl…resolvedOptions().timeZone`. Backend delivery_schedule validation
 * expects the modern Zoneinfo names (e.g. Asia/Calcutta → Asia/Kolkata).
 */
var IANA_TIMEZONE_ALIASES_FOR_API = {
  'asia/saigon': 'Asia/Ho_Chi_Minh',
  'asia/calcutta': 'Asia/Kolkata',
  'asia/katmandu': 'Asia/Kathmandu',
  'asia/rangoon': 'Asia/Yangon',
  'asia/chungking': 'Asia/Chongqing',
  'asia/ujung_pandang': 'Asia/Makassar',
  'europe/kiev': 'Europe/Kyiv',
  'pacific/samoa': 'Pacific/Pago_Pago',
  'us/pacific-new': 'America/Los_Angeles'
}

function canonicalIanaTimezoneForApi (tz) {
  if (typeof tz !== 'string') return tz
  var trimmed = tz.trim()
  if (!trimmed) return trimmed
  var mapped = IANA_TIMEZONE_ALIASES_FOR_API[trimmed.toLowerCase()]
  return mapped || trimmed
}

function browserIanaTimezoneStatic () {
  try {
    var tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (typeof tz === 'string' && tz.trim()) return canonicalIanaTimezoneForApi(tz)
  } catch (e) {}
  return 'UTC'
}

function scheduleTimezoneFromDeliverySchedule (ds) {
  if (!ds || typeof ds !== 'object') return ''
  var t = ds.timezone != null ? ds.timezone : ds.time_zone
  if (typeof t !== 'string' || !t.trim()) return ''
  return canonicalIanaTimezoneForApi(t)
}

function timeZonesEquivalentForUi (a, b) {
  if (!a || !b) return false
  var ca = canonicalIanaTimezoneForApi(String(a))
  var cb = canonicalIanaTimezoneForApi(String(b))
  if (ca.toLowerCase() === cb.toLowerCase()) return true
  var na = ca.toLowerCase()
  var nb = cb.toLowerCase()
  var utcish = ['utc', 'etc/utc', 'gmt', 'etc/gmt', 'zulu']
  return utcish.indexOf(na) >= 0 && utcish.indexOf(nb) >= 0
}

/** Calendar Y / zero-based month / D for `ms` interpreted in `iana`. */
function calendarPartsInTimeZone (ms, iana) {
  var dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: iana,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  var parts = {}
  dtf.formatToParts(new Date(ms)).forEach(function (p) {
    if (p.type !== 'literal') parts[p.type] = p.value
  })
  return { y: +parts.year, m0: +parts.month - 1, d: +parts.day }
}

/** UTC ms when it is `hour`:`minute` on calendar y-m0-d in `iana` (wall clock). */
function utcMillisForWallClockOnCalendarDateInIana (y, month0, day, hour, minute, iana) {
  var est = Date.UTC(y, month0, day, 12, 0, 0, 0)
  var lo = est - 36 * 3600000
  var hi = est + 36 * 3600000
  var dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: iana,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    second: '2-digit'
  })
  function partsAt (utcMs) {
    var parts = {}
    dtf.formatToParts(new Date(utcMs)).forEach(function (p) {
      if (p.type !== 'literal') parts[p.type] = p.value
    })
    var ph = +parts.hour
    if (ph === 24) ph = 0
    return { y: +parts.year, m0: +parts.month - 1, d: +parts.day, h: ph, min: +parts.minute }
  }
  for (var i = 0; i < 48; i++) {
    var mid = Math.floor((lo + hi) / 2)
    var pt = partsAt(mid)
    var dayCmp = (pt.y - y) * 400 + (pt.m0 - month0) * 40 + (pt.d - day)
    if (dayCmp !== 0) {
      if (dayCmp < 0) lo = mid + 1
      else hi = mid - 1
      continue
    }
    var hm = (pt.h - hour) * 3600000 + (pt.min - minute) * 60000
    if (hm === 0) return mid
    if (hm < 0) lo = mid + 1
    else hi = mid - 1
  }
  return Math.floor((lo + hi) / 2)
}

function upcomingCalendarPartsForWeekdayInIana (weekdayCanonical, iana) {
  var want = normalizeDeliveryWeekday(weekdayCanonical)
  for (var add = 0; add < 21; add++) {
    var cap = calendarPartsInTimeZone(Date.now() + add * 86400000, iana)
    var noonish = utcMillisForWallClockOnCalendarDateInIana(cap.y, cap.m0, cap.d, 12, 0, iana)
    var wdStr = new Intl.DateTimeFormat('en-US', { timeZone: iana, weekday: 'long' }).format(new Date(noonish))
    if (wdStr === want) return cap
  }
  return calendarPartsInTimeZone(Date.now(), iana)
}

/**
 * API stores digest hour in `delivery_schedule.timezone`. Convert to browser-local
 * wall-clock hour (0–23) for the time dropdown. If schedule has no timezone or matches
 * browser, return `scheduleHour` unchanged (legacy / same-zone).
 */
function deliveryScheduleHourToBrowserLocalHour (scheduleHour, deliverySchedule, freq, weekdayFromSchedule) {
  var h = Math.floor(Number(scheduleHour))
  if (isNaN(h) || h < 0 || h > 23) return scheduleHour
  var srcTz = scheduleTimezoneFromDeliverySchedule(deliverySchedule)
  var browserTz = browserIanaTimezoneStatic()
  if (!srcTz || timeZonesEquivalentForUi(srcTz, browserTz)) return h
  var ymd
  if (String(freq || '').toLowerCase() === 'weekly' && weekdayFromSchedule != null && String(weekdayFromSchedule).trim() !== '') {
    ymd = upcomingCalendarPartsForWeekdayInIana(weekdayFromSchedule, srcTz)
  } else {
    ymd = calendarPartsInTimeZone(Date.now(), srcTz)
  }
  try {
    var utcMs = utcMillisForWallClockOnCalendarDateInIana(ymd.y, ymd.m0, ymd.d, h, 0, srcTz)
    return new Date(utcMs).getHours()
  } catch (e) {
    return h
  }
}

/** GET /api/identity/users body → { email, id }[] (same idea as AccountTeamSettings `tenantUsers`). */
function identityUsersListFromBody (body) {
  var list = []
  if (Array.isArray(body)) list = body
  else if (body && Array.isArray(body.users)) list = body.users
  if (!list.length) return []
  return list.map(function (u) {
    var em = u.email || u.user_email || u.userEmail || ''
    var id = u.id != null ? u.id : (u.user_id != null ? u.user_id : (u.userId != null ? u.userId : null))
    if (id != null && typeof id === 'object' && id.value !== undefined) id = id.value
    return { email: String(em).trim(), id: id }
  }).filter(function (x) { return x.email })
}

/** Prefer `/api/identity/users` id per email so PUT `user_id` matches the selected member (user_account.user_id can be wrong). */
function mergeUserIdsFromIdentityUsers (emailRows, identityUsersBody) {
  var tenant = identityUsersListFromBody(identityUsersBody)
  if (!tenant.length || !emailRows || !emailRows.length) return
  emailRows.forEach(function (ea) {
    var ek = normalizeEmailKeyStatic(ea.email)
    if (!ek) return
    for (var i = 0; i < tenant.length; i++) {
      var tu = tenant[i]
      if (normalizeEmailKeyStatic(tu.email) === ek && tu.id != null && tu.id !== '') {
        ea.userId = tu.id
        return
      }
    }
  })
}

function augmentEmailRows (ea, apiWdts) {
  ea.isNew = !!ea.isNew
  ea.isDeleted = !!ea.isDeleted
  ea.originalEmail = ea.email
  ea.originalSubscribedToDailyDigestEmail = ea.subscribedToDailyDigestEmail
  ea.originalSubscribedToNewsletter = ea.subscribedToNewsletter
  ea.originalTimezoneOffset = ea.timezoneOffset
  ea.originalTimezoneAdjustToDST = ea.timezoneAdjustToDST
  ea.originalDigestEmailHour = ea.digestEmailHour
  if (!ea.unsubscribedSellerAccounts) ea.unsubscribedSellerAccounts = []
  ea.originalUnsubscribedSellerAccounts = ea.unsubscribedSellerAccounts.map(function (x) { return x })
  if (!ea.allowedSellerAccounts) ea.allowedSellerAccounts = []
  ea.originalallowedSellerAccounts = ea.allowedSellerAccounts.map(function (x) { return x })
  if (!ea.alertsSettings) ea.alertsSettings = {}
  var merge = apiWdts.slice()
  merge.forEach(function (w) {
    if (!ea.alertsSettings[w]) {
      ea.alertsSettings[w] = { RED: false, AMBER: false, GREEN: false }
    }
    var wd = ea.alertsSettings[w]
    wd.orig = { RED: !!wd.RED, AMBER: !!wd.AMBER, GREEN: !!wd.GREEN }
  })
  ea.alertChanged = function () {
    var ch = false
    Object.keys(ea.alertsSettings).forEach(function (key) {
      var wd = ea.alertsSettings[key]
      if (String(key).indexOf('__LOCAL__') === 0) return
      if (!wd || !wd.orig) return
      ;['RED', 'AMBER', 'GREEN'].forEach(function (k) {
        if (wd[k] !== wd.orig[k]) ch = true
      })
    })
    return ch
  }
  ea.subscribedChanged = function () {
    var o = new Set(ea.originalUnsubscribedSellerAccounts)
    var c = new Set(ea.unsubscribedSellerAccounts || [])
    return o.size === c.size && Array.from(o).every(function (id) { return c.has(id) })
  }
  ea.sellerAccessChanged = function () {
    var o = new Set(ea.originalallowedSellerAccounts)
    var c = new Set(ea.allowedSellerAccounts || [])
    return o.size === c.size && Array.from(o).every(function (id) { return c.has(id) })
  }
  ea.isDirty = function () {
    return (
      this.subscribedToDailyDigestEmail !== this.originalSubscribedToDailyDigestEmail ||
      this.subscribedToNewsletter !== this.originalSubscribedToNewsletter || this.isNew || this.isDeleted ||
      this.email !== this.originalEmail || this.digestEmailHour !== this.originalDigestEmailHour ||
      this.timezoneOffset !== this.originalTimezoneOffset || this.timezoneAdjustToDST !== this.originalTimezoneAdjustToDST ||
      this.alertChanged() || !this.subscribedChanged() || !this.sellerAccessChanged()
    )
  }
  return ea
}

export default {
  name: 'AlertSettings',
  data: function () {
    return {
      loading: false,
      loadError: null,
      isOwner: false,
      loginEmail: '',
      firstName: '',
      lastName: '',
      emailAddresses: [],
      sellers: [],
      packageDetails: null,
      watchDogTypes: [],
      selectedUserEmail: '',
      saving: false,
      successMsg: '',
      errorMsg: '',
      rowDelivery: {},
      /** Per alert row id → digest hour (6–22). Avoids sharing one `digestEmailHour` across daily/weekly rows. */
      rowDigestHour: {},
      /** Per alert row id → delay_minutes for buybox_lost (null = immediate). */
      rowDelayMinutes: {},
      /** Per alert row id → draft string shown in the delay input. */
      rowDelayDraft: {},
      /** Per alert row id → validation error for delay input. */
      rowDelayError: {},
      /** Per alert row id → Custom delay mode selected (vs Immediate). */
      rowDelayCustomOn: {},
      digestHourOptions: [],
      weeklyDay: {},
      lsPrefix: 'als_dm:',
      localRowOn: {},
      /** JSON snapshot of last saved / loaded prefs (for dirty detection) */
      prefsSnapshot: '',
      /** keyed by lowercase email → preferences[] from GET .../notification-preferences/users */
      notificationPrefsByUser: {},
      notificationPrefsBulkLoaded: false,
      /** keyed by lowercase email → { assignedSellerIds: [], availableSellers: [] } from notification-preferences APIs */
      notificationScopeByUser: {},
      offerActivityDigest: false,
      /** Inbox mode: false = separate emails, true = one thread per alert type (tenants.threaded_alerts). */
      threadedAlerts: false,
      inboxModeSaving: false,
      /** Owner / admins only (the account-wide setting); members see it read-only. */
      canEditThreadedAlerts: true,
      showInboxMode: true,
      inboxModeOptions: [
        { value: false, label: 'Separate emails', hint: 'One email per alert.' },
        { value: true, label: 'Threaded', hint: 'Alerts stack into one conversation per alert type. A new conversation starts after 100 alerts.' }
      ],
      /** Tenant feature flags (GET /api/identity/settings) that show extra alert rows. */
      competitivePriceAlerts: false,
      digestSaving: false,
      notificationLocale: 'en',
      supportedNotificationLocales: ['en', 'fr', 'de', 'it', 'es', 'pt', 'nl', 'pl'],
      localeSaving: false,
      localeError: null,
      localeSaved: false,
      /** Per alert row id → seller_ids for the selected user ([] = all storefronts). */
      rowSellerIds: {},
      /** Row id whose Storefronts dropdown is open (one at a time). */
      openStorefrontRowId: null,
      /** Per alert row id → inline error in the Storefronts dropdown. */
      storefrontMenuError: {},
      /** Alert (BRAND_FILTER_ALERTS) → seller id (string) → brand allowlist. The selected user's own lists (their emails only). */
      brandAllowlist: emptyBrandAllowlists(),
      /** JSON of brandAllowlist as last loaded / saved (dirty detection). */
      brandAllowlistSnapshot: JSON.stringify(emptyBrandAllowlists()),
      brandAllowlistLoaded: false,
      /** The selected user's default alert marketplaces: seller id (string) → 'all' | [codes]. Missing = all. */
      defaultMpScope: {},
      /** JSON of normalizeMpScope(defaultMpScope) as last loaded / saved (dirty detection). */
      defaultMpScopeSnapshot: '{}',
      /** Row id → that alert's own marketplaces (same shape), or null = follows the default. */
      rowMpScope: {},
      /** Row id → whether its Marketplaces box is expanded. */
      mpBoxOpen: {},
      /** Row id → sub-type keys switched off (ALERT_SUBTYPES); missing / [] = all on. */
      rowMutedSubtypes: {},
      /** Row id → whether its "Alert me about" box is expanded. */
      subtypeBoxOpen: {},
      /** Row id → message when the user tries to untick the last type. */
      subtypeError: {},
      /** Row id → the preference's `settings` object (rows with row.settingsKey only). */
      rowSettings: {},
      /** Row id → whether its Sensitivity box is expanded. */
      settingsBoxOpen: {},
      /** Alert → whether its Brand filter section is expanded (default: open until a filter exists). */
      brandFilterOpen: {},
      /** `${alert}|${sellerId}` whose “+ Add brand” picker is open (one at a time). */
      brandPickerKey: null,
      brandQuery: '',
      /** `${sellerId}|${query}` → [{ brand, asin_count }] from GET /api/v1/sellers/:id/brands */
      brandOptionsCache: {},
      brandOptionsLoading: false,
      brandQueryTimer: null
    }
  },
  computed: {
    notificationLocaleOptions: function () {
      var codes = Array.isArray(this.supportedNotificationLocales) && this.supportedNotificationLocales.length
        ? this.supportedNotificationLocales
        : ['en', 'fr', 'de', 'it', 'es', 'pt', 'nl', 'pl']
      return codes.map(function (code) {
        return { code: code, label: NOTIFICATION_LOCALE_LABELS[code] || code.toUpperCase() }
      })
    },
    isProPlan: function () {
      if (!this.packageDetails) return false
      var name = (this.packageDetails.name || this.packageDetails.packageName || '').toLowerCase()
      if (name.indexOf('pro') >= 0) return true
      var cents = this.packageDetails.baseSubscriptionFeeCents
      return cents != null && cents >= 4900
    },
    selectedEa: function () {
      var self = this
      var key = self.normalizeEmailKey(self.selectedUserEmail)
      return this.emailAddresses.filter(function (ea) {
        return !ea.isDeleted && self.normalizeEmailKey(ea.email) === key
      })[0] || null
    },
    dropdownUsers: function () {
      var self = this
      var users = []
      this.emailAddresses.forEach(function (ea) {
        if (ea.isDeleted || !ea.email) return
        users.push({
          email: ea.email,
          firstName: ea.firstName || '',
          lastName: ea.lastName || '',
          fullName: ea.fullName || '',
          isOwner: !!(ea.isPrimary || userAccountRowIsAdmin(ea)),
          is_owner: ea.is_owner === true || ea.isOwner === true,
          role: ea.role != null ? ea.role : 'member'
        })
      })
      if (!this.isOwner && this.loginEmail) {
        var loginKey = self.normalizeEmailKey(self.loginEmail)
        return users.filter(function (u) { return self.normalizeEmailKey(u.email) === loginKey })
      }
      return users
    },
    /** Storefronts assigned to the selected user (nickname + marketplace flags). */
    assignedStorefronts: function () {
      var ea = this.selectedEa
      if (!ea) return []
      var assignedSet = this.assignedSellerIdSetForSelectedUser()
      var list = this.availableSellersForSelectedUser().filter(function (s) {
        var sid = s.sellerId && s.sellerId.value !== undefined ? s.sellerId.value : s.sellerId
        return assignedSet.has(String(sid))
      })
      return list.map(function (s, idx) {
        var sid = s.sellerId && s.sellerId.value !== undefined ? s.sellerId.value : s.sellerId
        var codes
        if (Array.isArray(s.marketplaceCodes) && s.marketplaceCodes.length) {
          codes = s.marketplaceCodes.map(function (c) { return String(c).toLowerCase() })
        } else {
          var mp = (s.marketplaceIso31662Code || REGION_MARKETPLACE[s.region] || 'us').toLowerCase()
          if (mp === 'us' || (s.region || '') === 'NA') codes = ['us', 'ca', 'mx']
          else if (mp === 'gb' || (s.region || '') === 'EU') codes = ['gb', 'de', 'fr']
          else codes = [mp === 'gb' ? 'gb' : mp]
        }
        return {
          key: String(sid) + '-' + idx,
          id: Number(sid),
          name: s.nickName || ('Storefront ' + String(sid).slice(-6)),
          mpCodes: codes
        }
      })
    },
    /** Users edit their own filter; owners/admins also edit other users'. */
    brandFilterEditable: function () {
      return !!this.isOwner || this.selectedUserEmail === this.loginEmail
    },
    brandAllowlistChanged: function () {
      return this.serializedBrandAllowlist() !== this.brandAllowlistSnapshot
    },
    /** Marketplace choices only matter when some storefront sells in more than one marketplace. */
    hasMultiMp: function () {
      var self = this
      return this.assignedStorefronts.some(function (sf) { return self.sfMpCodes(sf).length > 1 })
    },
    mpStorefrontsAll: function () {
      var self = this
      return this.assignedStorefronts.filter(function (sf) { return self.sfMpCodes(sf).length > 1 })
    },
    /** Same rule as the notification language: owners/admins, or the user themselves. */
    mpDefaultEditable: function () {
      return !!this.isOwner || this.selectedUserEmail === this.loginEmail
    },
    defaultMpScopeChanged: function () {
      return JSON.stringify(normalizeMpScope(this.defaultMpScope)) !== this.defaultMpScopeSnapshot
    },
    categorizedRows: function () {
      var map = {}; var grouped = []; var idx = {}
      var self = this
      ROWS.forEach(function (row, ord) {
        if (!self.rowAvailable(row)) return
        if (!idx[row.category]) idx[row.category] = 0
        var r = Object.assign({}, row, { _sort: idx[row.category]++ })
        if (!map[row.category]) {
          map[row.category] = { name: row.category, rows: [] }
          grouped.push(map[row.category])
        }
        map[row.category].rows.push(Object.assign({}, r, { _ord: ord }))
      })
      grouped.forEach(function (cat) {
        cat.rows.sort(function (a, b) { return a._ord - b._ord })
      })
      return grouped
    },
    hasDelayValidationError: function () {
      var errs = this.rowDelayError || {}
      return Object.keys(errs).some(function (k) { return !!errs[k] })
    },
    hasChanges: function () {
      if (this.loading) return false
      if (this.hasDelayValidationError) return true
      if (this.brandAllowlistChanged) return true
      if (this.defaultMpScopeChanged) return true
      var cur = this.serializedPrefsForCompare()
      if (!this.prefsSnapshot) return false
      return cur !== this.prefsSnapshot
    },
    weekDaysList: function () {
      return WEEKLY_SCHEDULE_DAYS
    },
    weeklyScheduleDayDefault: function () {
      return WEEKLY_SCHEDULE_DAY_DEFAULT
    },
    /** Rows sent to /api/v1/notification-preferences (shown in coverage pill). */
    alertTypesCount: function () {
      var self = this
      return ROWS.filter(function (r) { return !!r.alertCategory && self.rowAvailable(r) }).length
    }
  },
  watch: {
    hasChanges: function (v) {
      window.dirty = !!v
    },
    selectedUserEmail: function () {
      var self = this
      self.rowSellerIds = {}
      self.rowMpScope = {}
      self.mpBoxOpen = {}
      self.rowMutedSubtypes = {}
      self.subtypeBoxOpen = {}
      self.subtypeError = {}
      self.rowSettings = {}
      self.settingsBoxOpen = {}
      self.openStorefrontRowId = null
      self.storefrontMenuError = {}
      self.hydrateDeliveryFromLs()
      self.hydrateLocalRowToggles()
      self.loadBrandAllowlists()
      self.loadNotificationPreferencesFromApi({ forceUserGet: true }).then(function () {
        self.capturePrefsSnapshot()
      }).catch(function () {
        self.capturePrefsSnapshot()
      })
    }
  },
  created: function () {
    var hrs = []
    for (var h = 0; h <= 23; h++) hrs.push(h)
    this.digestHourOptions = hrs
    window.dirty = false
  },
  mounted: function () {
    this.fetchAll().catch(function () {})
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.onDocumentClick)
    if (this.brandQueryTimer) clearTimeout(this.brandQueryTimer)
  },
  beforeRouteLeave: function (to, from, next) {
    if (window.dirty) {
      window.confirm('You have unsaved changes. Leave anyway?') ? (window.dirty = false, next()) : next(false)
    } else next()
  },
  methods: {
    rowLocked: function (row) {
      return !!(row.lockedStarter && !this.isProPlan)
    },
    /** Static “Real-time” badge only — no Daily/Weekly cadence control for this row. */
    deliveryRealtimeBadgeOnly: function (row) {
      return !!row.deliveryRealtimeBadgeOnly
    },
    /** Cadences a row offers; rows can narrow it (row.deliveryOptions), e.g. No Featured Offer has no weekly. */
    deliveryOptionsForRow: function (row) {
      return (row && row.deliveryOptions) || ['realtime', 'daily', 'weekly']
    },
    fetchAll: function () {
      var self = this
      this.loading = true
      this.loadError = null
      return Promise.all([
        self.$http.get('/api/identity/settings'),
        self.$http.get('/api/identity/users').catch(function () {
          return { body: null }
        }),
        self.$http.get('/api/billing/payment-status', {
          params: { timezoneOffsetMinutes: 0 - new Date().getTimezoneOffset() }
        }).catch(function () {
          return { body: null }
        })
      ]).then(function (results) {
        self.loading = false
        var body = results[0].body || {}
        var identityUsersBody = results[1] ? results[1].body : null
        var digestFlag = body.offerActivityDigest
        if (digestFlag == null) digestFlag = body.offer_activity_digest
        self.offerActivityDigest = digestFlag === true || digestFlag === 1 || digestFlag === '1' || digestFlag === 'true'
        self.threadedAlerts = body.threadedAlerts === true
        self.canEditThreadedAlerts = body.canEditThreadedAlerts !== false
        self.competitivePriceAlerts = body.competitivePriceAlerts === true
        self.loginEmail = body.email || ''
        self.isOwner = !!body.isOwner
        self.firstName = body.firstName || ''
        self.lastName = body.lastName || ''
        var rawWdts = Object.keys(body.watchDogTypesToDisplayName || {}).filter(function () { return true })
        var base = ['BAD_REVIEW', 'TOP_REVIEW', 'PRODUCT_DETAILS_RECENT_CHANGE', 'PRODUCT_CATEGORY_RECENT_CHANGE', 'LOST_BUYBOX', 'NO_ADD_TO_CART', 'PRICE', 'KEYWORD', 'INVENTORY', 'FBM_INVENTORY', 'SALES_DROP']
        rawWdts.forEach(function (k) {
          if (base.indexOf(k) < 0) base.push(k)
        })
        self.watchDogTypes = base
        var rawEa = Array.isArray(body.user_account) ? body.user_account.map(function (ua) {
          var fn = ua.first_name != null ? ua.first_name : ua.firstName
          var ln = ua.last_name != null ? ua.last_name : ua.lastName
          var full = ua.full_name != null ? ua.full_name : ua.fullName
          if (!full && (fn || ln)) full = [fn, ln].filter(Boolean).join(' ')
          var sellerIds = Array.isArray(ua.sellerIds) ? ua.sellerIds : (Array.isArray(ua.seller_ids) ? ua.seller_ids : [])
          var emailAddrId = ua.emailAddressId != null ? ua.emailAddressId : (ua.email_address_id != null ? ua.email_address_id : null)
          if (emailAddrId == null && ua.id != null) emailAddrId = ua.id
          return {
            email: ua.email || '',
            firstName: fn || '',
            lastName: ln || '',
            fullName: full || '',
            userId: ua.userId != null ? ua.userId : ua.user_id,
            emailAddressId: emailAddrId,
            role: ua.role != null ? ua.role : 'member',
            is_owner: ua.is_owner === true || ua.isOwner === true,
            isPrimary: userAccountRowIsAdmin(ua),
            allowedSellerAccounts: sellerIds.map(function (id) { return id }),
            unsubscribedSellerAccounts: [],
            alertsSettings: {},
            emailVerified: userAccountEmailVerifiedFromSettings(ua)
          }
        }) : []
        rawEa.forEach(function (ea) {
          if (ea.isPrimary === undefined && ea.is_primary !== undefined) ea.isPrimary = !!ea.is_primary
          if (ea.emailAddressId == null && ea.email_address_id != null) ea.emailAddressId = ea.email_address_id
          if (ea.emailAddressId == null && ea.id != null) ea.emailAddressId = ea.id
          if (ea.allowedSellersAccount && !ea.allowedSellerAccounts) ea.allowedSellerAccounts = ea.allowedSellersAccount
          if (!ea.allowedSellerAccounts) ea.allowedSellerAccounts = []
        })
        mergeUserIdsFromIdentityUsers(rawEa, identityUsersBody)
        self.sellers = Array.isArray(body.sellerAccounts) ? body.sellerAccounts : []
        self.emailAddresses = rawEa.map(function (ea) { return augmentEmailRows(ea, self.watchDogTypes) }).sort(function (a, b) {
          if (a.isPrimary && !b.isPrimary) return -1
          if (!a.isPrimary && b.isPrimary) return 1
          return String(a.email).localeCompare(String(b.email))
        })
        self.resolveInitialSelectedUserEmail()
        self.hydrateDeliveryFromLs()
        self.hydrateLocalRowToggles()
        self.loadBrandAllowlists()
        var pack = results[2] && results[2].body
        self.packageDetails = pack && pack.packageDetails ? pack.packageDetails : null
        if (self.isOwner) {
          return self.fetchNotificationPreferencesUsersBulk()
            .catch(function () {})
            .then(function () {
              return self.loadNotificationPreferencesFromApi()
            })
            .then(function () {
              self.capturePrefsSnapshot()
            })
        }
        return self.loadNotificationPreferencesFromApi({ forceUserGet: true })
          .then(function () {
            self.capturePrefsSnapshot()
          })
      }).catch(function (err) {
        self.loading = false
        self.loadError = (err.body && err.body.message) || 'Failed to load'
        throw err
      })
    },
    ensureWd: function (ea, wdt) {
      if (!wdt || !ea || !ea.alertsSettings) return { RED: false, AMBER: false, GREEN: false }
      if (!ea.alertsSettings[wdt]) {
        this.$set(ea.alertsSettings, wdt, { RED: false, AMBER: false, GREEN: false, orig: { RED: false, AMBER: false, GREEN: false } })
      }
      var wd = ea.alertsSettings[wdt]
      if (!wd.orig) wd.orig = { RED: !!wd.RED, AMBER: !!wd.AMBER, GREEN: !!wd.GREEN }
      return wd
    },
    hydrateLocalRowToggles: function () {
      var self = this
      self.localRowOn = {}
      try {
        var store = JSON.parse(window.localStorage.getItem('als_local_row_on') || '{}')
        var userMap = store[self.selectedUserEmail] || {}
        ROWS.forEach(function (row) {
          if (!row.localKey) return
          if (userMap[row.id] === true || userMap[row.id] === false) {
            self.$set(self.localRowOn, row.id, userMap[row.id] === true)
          } else {
            // Digest / formerly always-on rows default on until API/LS says otherwise
            self.$set(self.localRowOn, row.id, row.digestFixed === true || row.defaultOn === true)
          }
        })
      } catch (e) {}
    },
    persistLocalRowOnJson: function () {
      var self = this
      try {
        var store = JSON.parse(window.localStorage.getItem('als_local_row_on') || '{}')
        var nest = {}
        ROWS.forEach(function (row) {
          if (!row.localKey) return
          nest[row.id] = self.localRowOn[row.id] === true
        })
        store[self.selectedUserEmail] = nest
        window.localStorage.setItem('als_local_row_on', JSON.stringify(store))
      } catch (e) {}
    },
    rowOn: function (row) {
      var ea = this.selectedEa
      if (!ea || row.alwaysOn) return false
      if (this.rowLocked(row)) return false
      if (row.localKey) {
        return this.localRowOn[row.id] === true
      }
      if (!row.wdt) return false
      var wd = this.ensureWd(ea, row.wdt)
      return !!(wd.RED || wd.AMBER || wd.GREEN)
    },
    onRowToggle: function (row, on) {
      var ea = this.selectedEa
      if (!ea || row.alwaysOn) return
      if (this.rowLocked(row)) return
      if (row.localKey) {
        this.$set(this.localRowOn, row.id, on)
        this.persistLocalRowOnJson()
        return
      }
      if (!row.wdt) return
      var wd = this.ensureWd(ea, row.wdt)
      if (!wd.orig) wd.orig = { RED: wd.RED, AMBER: wd.AMBER, GREEN: wd.GREEN }
      wd.RED = wd.AMBER = wd.GREEN = on
    },
    hydrationKey: function (rid) {
      return this.selectedUserEmail + '|' + rid
    },
    /** Owners default to primary account; members always load their own login email. */
    resolveInitialSelectedUserEmail: function () {
      var self = this
      if (!self.isOwner && self.loginEmail) {
        var loginKey = self.normalizeEmailKey(self.loginEmail)
        var match = self.emailAddresses.filter(function (ea) {
          return !ea.isDeleted && self.normalizeEmailKey(ea.email) === loginKey
        })[0]
        self.selectedUserEmail = match ? match.email : self.loginEmail
        return
      }
      var primary = self.emailAddresses.filter(function (e) { return e.isPrimary })[0]
      self.selectedUserEmail = (primary || self.emailAddresses[0] || {}).email || self.loginEmail || ''
    },
    /** Numeric / string user id for API query params (from `user_account` / selected row). */
    selectedUserIdForApi: function () {
      var ea = this.selectedEa
      if (!ea) return null
      var raw = ea.userId != null ? ea.userId : ea.user_id
      if (raw == null || raw === '') return null
      if (typeof raw === 'object' && raw.value !== undefined) return raw.value
      return raw
    },
    /** Sync rowDelivery / weeklyDay / rowDigestHour / rowDelayMinutes from LS for the selected user — always $sets so stale keys cannot leak across team members and Vue tracks rowDelivery reliably. */
    hydrateDeliveryFromLs: function () {
      var self = this
      ROWS.forEach(function (row) {
        if (row.alwaysOn || row.digestFixed) return
        try {
          var dm = window.localStorage.getItem(self.lsPrefix + self.hydrationKey(row.id))
          if (dm === 'daily' || dm === 'weekly' || dm === 'realtime') {
            self.$set(self.rowDelivery, row.id, dm)
          } else {
            self.$set(self.rowDelivery, row.id, 'realtime')
          }
          var wd = window.localStorage.getItem('als_wday|' + self.hydrationKey(row.id))
          self.$set(self.weeklyDay, row.id, normalizeDeliveryWeekday(wd || WEEKLY_SCHEDULE_DAY_DEFAULT))
        } catch (e) {
          self.$set(self.rowDelivery, row.id, 'realtime')
        }
      })
      ROWS.forEach(function (row) {
        if (row.alwaysOn && !row.digestFixed) return
        try {
          var hk = window.localStorage.getItem('als_hour|' + self.hydrationKey(row.id))
          if (hk != null && hk !== '') {
            var nh = Number(hk)
            if (!isNaN(nh)) self.$set(self.rowDigestHour, row.id, nh)
            else self.$set(self.rowDigestHour, row.id, undefined)
          } else {
            self.$set(self.rowDigestHour, row.id, undefined)
          }
        } catch (e2) {
          self.$set(self.rowDigestHour, row.id, undefined)
        }
      })
      ROWS.forEach(function (row) {
        if (!row.delayMinutesConfigurable) return
        try {
          var delayRaw = window.localStorage.getItem('als_delay|' + self.hydrationKey(row.id))
          var delayN = normalizeBuyboxDelayMinutes(delayRaw)
          self.applyBuyboxDelayState(row, delayN)
        } catch (e3) {
          self.applyBuyboxDelayState(row, null)
        }
      })
    },
    delayMinutesForRow: function (row) {
      if (!row || !row.delayMinutesConfigurable) return null
      return normalizeBuyboxDelayMinutes(this.rowDelayMinutes[row.id])
    },
    delayDraftForRow: function (row) {
      if (!row) return ''
      var d = this.rowDelayDraft[row.id]
      if (d != null) return d
      var n = this.delayMinutesForRow(row)
      return n == null ? '' : String(n)
    },
    delayErrorForRow: function (row) {
      if (!row) return ''
      return this.rowDelayError[row.id] || ''
    },
    isBuyboxDelayCustom: function (row) {
      if (!row || !row.delayMinutesConfigurable) return false
      if (this.rowDelayCustomOn[row.id] === true) return true
      return this.delayMinutesForRow(row) != null
    },
    applyBuyboxDelayState: function (row, delayN) {
      if (!row || !row.delayMinutesConfigurable) return
      var n = normalizeBuyboxDelayMinutes(delayN)
      this.$set(this.rowDelayMinutes, row.id, n)
      this.$set(this.rowDelayError, row.id, '')
      if (n == null) {
        this.$set(this.rowDelayCustomOn, row.id, false)
        this.$set(this.rowDelayDraft, row.id, '')
      } else {
        this.$set(this.rowDelayCustomOn, row.id, true)
        this.$set(this.rowDelayDraft, row.id, String(n))
      }
      try {
        window.localStorage.setItem('als_delay|' + this.hydrationKey(row.id), n == null ? '' : String(n))
      } catch (e) {}
    },
    setBuyboxDelayImmediate: function (row) {
      if (!row || !row.delayMinutesConfigurable) return
      this.applyBuyboxDelayState(row, null)
    },
    /** Switch from Immediate → Custom delay input (defaults to 20 min). */
    enableBuyboxDelayInput: function (row) {
      if (!row || !row.delayMinutesConfigurable) return
      if (this.isBuyboxDelayCustom(row) && this.delayMinutesForRow(row) != null && !this.delayErrorForRow(row)) return
      this.applyBuyboxDelayState(row, 20)
    },
    onBuyboxDelayInput: function (row, raw) {
      if (!row || !row.delayMinutesConfigurable) return
      this.$set(this.rowDelayCustomOn, row.id, true)
      this.$set(this.rowDelayDraft, row.id, raw == null ? '' : String(raw))
      var parsed = validateBuyboxDelayMinutesInput(raw)
      if (parsed.error) {
        this.$set(this.rowDelayError, row.id, parsed.error)
        return
      }
      this.$set(this.rowDelayError, row.id, '')
      this.$set(this.rowDelayMinutes, row.id, parsed.value)
      try {
        window.localStorage.setItem('als_delay|' + this.hydrationKey(row.id), String(parsed.value))
      } catch (e) {}
    },
    digestHourForRow: function (rowId) {
      var h = this.rowDigestHour[rowId]
      if (h == null || h === '') return 8
      var n = Number(h)
      return isNaN(n) ? 8 : n
    },
    setDigestHourForRow: function (rowId, hour) {
      var n = Number(hour)
      if (isNaN(n)) return
      this.$set(this.rowDigestHour, rowId, n)
      try {
        window.localStorage.setItem('als_hour|' + this.hydrationKey(rowId), String(n))
      } catch (e) {}
      if (rowId === 'digest_summary' && this.selectedEa) {
        this.$set(this.selectedEa, 'digestEmailHour', n)
      }
    },
    /** Read only from reactive rowDelivery so segment buttons re-render when prefs load ($set); do not prefer localStorage here or Vue skips tracking rowDelivery. */
    deliveryFor: function (row) {
      if (row.alwaysOn) return 'realtime'
      var v = this.rowDelivery[row.id]
      if (row.deliveryOptions && row.deliveryOptions.indexOf(v) === -1) return 'realtime'
      if (v === 'daily' || v === 'weekly' || v === 'realtime') return v
      return 'realtime'
    },
    setDelivery: function (row, mode) {
      this.$set(this.rowDelivery, row.id, mode)
      try {
        window.localStorage.setItem(this.lsPrefix + this.hydrationKey(row.id), mode)
      } catch (e) {}
    },
    setWeeklyDay: function (rowId, v) {
      this.$set(this.weeklyDay, rowId, normalizeDeliveryWeekday(v))
      try {
        window.localStorage.setItem('als_wday|' + this.hydrationKey(rowId), v)
      } catch (e) {}
    },
    formatUserLabel: function (u) {
      var name = u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
      if (!u.isOwner) return name + ' — Member'
      var ws = u.is_owner === true
      var r = String(u.role || '').toLowerCase()
      var adminRole = r === 'admin' || r === 'owner'
      if (ws && adminRole) return name + ' — Admin & Owner'
      if (ws) return name + ' — Owner'
      return name + ' — Admin'
    },
    mpLabel: function (code) {
      return MP_LABELS[code] || String(code).toUpperCase()
    },
    mpFlagClass: function (code) {
      return code === 'gb' ? 'gb' : code
    },
    formatDigestHour: function (h) {
      var p = h >= 12 ? 'PM' : 'AM'
      var x = h > 12 ? h - 12 : (h === 0 ? 12 : h)
      return x + ':00 ' + p
    },
    serializedPrefsForCompare: function () {
      var arr = this.buildPreferencesArray()
      arr.sort(function (a, b) {
        return String(a.alert_category).localeCompare(String(b.alert_category))
      })
      return JSON.stringify(arr)
    },
    /** Rows behind a tenant feature flag (row.tenantFlag) only exist for tenants that have it. */
    rowAvailable: function (row) {
      return !row.tenantFlag || this[row.tenantFlag] === true
    },
    buildPreferencesArray: function () {
      var self = this
      var out = []
      ROWS.forEach(function (row) {
        if (!row.alertCategory) return
        // Rows behind a tenant flag are not sent while hidden, so a save never changes them.
        if (!self.rowAvailable(row)) return
        var hour = self.digestHourForRow(row.id)
        var d = self.deliveryApiForRow(row, hour)
        var pref = {
          alert_category: row.alertCategory,
          enabled: self.rowEnabledForPreference(row),
          seller_ids: self.rowSellerIdsFor(row).slice(),
          delivery_frequency: d.frequency,
          delivery_schedule: d.schedule
        }
        // buybox_lost only — null/omit = immediate; 20..40 otherwise
        if (row.delayMinutesConfigurable) {
          pref.delay_minutes = self.delayMinutesForRow(row)
        }
        // null = follow the default. Kept (and sent unchanged) while the alert is off.
        if (self.rowSupportsOwnMp(row)) {
          pref.marketplace_scope = normalizeMpScope(self.rowMpScope[row.id])
        }
        if (row.subtypes) {
          pref.muted_subtypes = self.mutedSubtypesFor(row).slice().sort()
        }
        if (row.settingsKey) {
          pref.settings = self.rowSettingsFor(row)
        }
        out.push(pref)
      })
      return out
    },
    /** seller_ids for one row, sorted ([] = all storefronts). Stale (unassigned) ids are kept so a save never widens. */
    rowSellerIdsFor: function (row) {
      var ids = row && this.rowSellerIds[row.id]
      if (!Array.isArray(ids)) return []
      return ids.slice().sort(function (a, b) { return a - b })
    },
    showStorefrontChip: function (row) {
      if (!row.alertCategory || row.alwaysOn || this.rowLocked(row)) return false
      if (!this.rowOn(row)) return false
      return this.assignedStorefronts.length > 1
    },
    selectedAssignedStorefronts: function (row) {
      var ids = this.rowSellerIdsFor(row)
      if (!ids.length) return this.assignedStorefronts
      return this.assignedStorefronts.filter(function (sf) { return ids.indexOf(sf.id) >= 0 })
    },
    storefrontChipLabel: function (row) {
      if (!this.rowSellerIdsFor(row).length) return 'All storefronts'
      return this.selectedAssignedStorefronts(row).length + ' of ' + this.assignedStorefronts.length + ' storefronts'
    },
    storefrontChipTooltip: function (row) {
      if (!this.rowSellerIdsFor(row).length) return 'All storefronts'
      var names = this.selectedAssignedStorefronts(row).map(function (sf) { return sf.name })
      return names.length ? names.join(', ') : 'No assigned storefronts selected'
    },
    /** Narrowed to storefronts that are no longer assigned to this user — the alert sends nothing. */
    storefrontSelectionStale: function (row) {
      if (!row.alertCategory || row.alwaysOn || this.rowLocked(row) || !this.rowOn(row)) return false
      if (!this.rowSellerIdsFor(row).length) return false
      return this.selectedAssignedStorefronts(row).length === 0
    },
    storefrontSelected: function (row, sellerId) {
      var ids = this.rowSellerIdsFor(row)
      return ids.length > 0 && ids.indexOf(sellerId) >= 0
    },
    toggleStorefrontMenu: function (row) {
      this.brandPickerKey = null
      this.$set(this.storefrontMenuError, row.id, '')
      this.openStorefrontRowId = this.openStorefrontRowId === row.id ? null : row.id
    },
    setRowSellerIds: function (row, ids) {
      var assignedIds = this.assignedStorefronts.map(function (sf) { return sf.id })
      var uniq = ids.filter(function (id, i) { return ids.indexOf(id) === i })
      var coversAll = assignedIds.length > 0 && assignedIds.every(function (id) { return uniq.indexOf(id) >= 0 })
      this.$set(this.rowSellerIds, row.id, coversAll ? [] : uniq)
      this.$set(this.storefrontMenuError, row.id, '')
    },
    setStorefrontsAll: function (row) {
      this.setRowSellerIds(row, [])
    },
    setStorefrontsOnly: function (row) {
      if (this.rowSellerIdsFor(row).length) return
      var first = this.assignedStorefronts[0]
      if (first) this.setRowSellerIds(row, [first.id])
    },
    onStorefrontCheck: function (row, sellerId, checked, evt) {
      var assignedIds = this.assignedStorefronts.map(function (sf) { return sf.id })
      var current = this.rowSellerIdsFor(row)
      // From "All", every box shows unticked; ticking one starts a narrowed list.
      var base = current.filter(function (id) { return assignedIds.indexOf(id) >= 0 })
      var next = checked
        ? base.concat([sellerId])
        : base.filter(function (id) { return id !== sellerId })
      if (!next.length) {
        if (evt && evt.target) evt.target.checked = true
        this.$set(this.storefrontMenuError, row.id, 'Pick at least one storefront, or turn the alert off.')
        return
      }
      this.setRowSellerIds(row, next)
    },
    onDocumentClick: function (e) {
      var t = e && e.target
      if (t && t.closest && (t.closest('.als-sf-chip') || t.closest('.als-bf-add'))) return
      this.openStorefrontRowId = null
      this.brandPickerKey = null
    },
    subtypeOptions: function (row) {
      return ALERT_SUBTYPES[row.subtypes] || []
    },
    mutedSubtypesFor: function (row) {
      var muted = this.rowMutedSubtypes[row.id]
      return Array.isArray(muted) ? muted : []
    },
    subtypeOn: function (row, key) {
      return this.mutedSubtypesFor(row).indexOf(key) < 0
    },
    /** Shown on the alert itself (always-on rows too) once it's on; hidden while off, value kept. */
    subtypeBoxVisible: function (row) {
      if (!row.subtypes || this.rowLocked(row)) return false
      return row.alwaysOn || this.rowOn(row)
    },
    subtypeSummary: function (row) {
      var all = this.subtypeOptions(row).length
      var on = all - this.mutedSubtypesFor(row).length
      return on === all ? 'all types' : on + ' of ' + all + ' types'
    },
    /** Unticking the last type is refused: turning the alert off is the way to get none. */
    onSubtypeCheck: function (row, key, checked, evt) {
      var muted = this.mutedSubtypesFor(row).filter(function (k) { return k !== key })
      if (!checked) muted = muted.concat([key])
      if (muted.length >= this.subtypeOptions(row).length) {
        if (evt && evt.target) evt.target.checked = true
        this.$set(this.subtypeError, row.id, row.alwaysOn
          ? 'Keep at least one type. This alert is always on.'
          : 'Keep at least one type, or turn the alert off.')
        return
      }
      this.$set(this.subtypeError, row.id, '')
      this.$set(this.rowMutedSubtypes, row.id, muted)
    },
    setAllSubtypes: function (row) {
      this.$set(this.subtypeError, row.id, '')
      this.$set(this.rowMutedSubtypes, row.id, [])
    },
    /** The row's `settings` with defaults filled in (rows with row.settingsKey only). */
    rowSettingsFor: function (row) {
      return normalizeRowSettings(row.settingsKey, this.rowSettings[row.id])
    },
    setRowSetting: function (row, key, value) {
      var next = this.rowSettingsFor(row)
      next[key] = value
      this.$set(this.rowSettings, row.id, normalizeRowSettings(row.settingsKey, next))
    },
    /** Whole numbers from 1 up; anything else snaps back to the last valid value. */
    onMinRatingCountDropChange: function (row, evt) {
      var n = parseInt(evt.target.value, 10)
      if (!isNaN(n) && n >= 1) this.setRowSetting(row, 'min_rating_count_drop', n)
      evt.target.value = this.rowSettingsFor(row).min_rating_count_drop
    },
    reviewSensitivitySummary: function (row) {
      var st = this.rowSettingsFor(row)
      var n = st.min_rating_count_drop
      return (st.rating_sensitivity === 'tenth' ? 'every 0.1 drop' : 'every half-star') +
        ', rating count drops of ' + n + (n === 1 ? ' or more' : '+')
    },
    /** A storefront's marketplaces as { raw (for flag/label), code (API form) }, deduped. */
    sfMpCodes: function (sf) {
      var seen = {}
      return (sf.mpCodes || []).reduce(function (acc, raw) {
        var code = mpApiCode(raw)
        if (code && !seen[code]) {
          seen[code] = true
          acc.push({ raw: raw, code: code })
        }
        return acc
      }, [])
    },
    mpEntry: function (scope, sf) {
      var v = scope ? scope[String(sf.id)] : undefined
      return Array.isArray(v) ? v : 'all'
    },
    mpIsAll: function (scope, sf) {
      return this.mpEntry(scope, sf) === 'all'
    },
    mpChipOn: function (scope, sf, code) {
      var v = this.mpEntry(scope, sf)
      return v !== 'all' && v.indexOf(code) >= 0
    },
    setMpAll: function (scope, sf) {
      if (scope) this.$set(scope, String(sf.id), 'all')
    },
    /** From All, a click picks just that code. Deselecting the last code, or picking every one, goes back to All. */
    toggleMpChip: function (scope, sf, code) {
      if (!scope) return
      var all = this.sfMpCodes(sf).map(function (m) { return m.code })
      var cur = this.mpEntry(scope, sf)
      var next
      if (cur === 'all') {
        next = [code]
      } else {
        next = cur.indexOf(code) >= 0
          ? cur.filter(function (c) { return c !== code })
          : cur.concat([code])
      }
      var known = next.filter(function (c) { return all.indexOf(c) >= 0 })
      if (!known.length || known.length === all.length) {
        this.$set(scope, String(sf.id), 'all')
      } else {
        this.$set(scope, String(sf.id), all.filter(function (c) { return next.indexOf(c) >= 0 }))
      }
    },
    /** Picked list with none of the storefront's current marketplaces (one was removed): sends nothing. Never widened silently. */
    mpStale: function (scope, sf) {
      var v = this.mpEntry(scope, sf)
      if (v === 'all') return false
      var codes = this.sfMpCodes(sf).map(function (m) { return m.code })
      return !v.some(function (c) { return codes.indexOf(c) >= 0 })
    },
    /** "UK" (one storefront) · "EU-1: UK, DE · NA-1: US" · "All marketplaces". */
    mpScopeSummary: function (scope, storefronts) {
      var self = this
      var parts = []
      ;(storefronts || []).forEach(function (sf) {
        var v = self.mpEntry(scope, sf)
        if (v === 'all') return
        var labels = v.map(function (c) { return MP_LABELS[c.toLowerCase()] || c })
        parts.push(storefronts.length > 1 || self.assignedStorefronts.length > 1 ? sf.name + ': ' + labels.join(', ') : labels.join(', '))
      })
      return parts.length ? parts.join(' · ') : 'All marketplaces'
    },
    /** Rows that can have their own marketplaces: not always-on, not fixed digests, not account-level, and not
     *  backend attributes (those emails are not filtered by marketplace yet). */
    rowSupportsOwnMp: function (row) {
      return !!row.alertCategory && !row.alwaysOn && !row.digestFixed && !row.noMarketplaceFilter &&
        NO_MARKETPLACE_CATEGORIES.indexOf(row.alertCategory) < 0
    },
    /** Storefronts this row covers that sell in more than one marketplace. */
    mpStorefronts: function (row) {
      var self = this
      return this.brandStorefronts(row).filter(function (sf) { return self.sfMpCodes(sf).length > 1 })
    },
    /** Hidden while the alert is off; its saved value is kept. */
    mpBoxVisible: function (row) {
      return this.rowSupportsOwnMp(row) && this.rowOn(row) && !this.rowLocked(row) && this.mpStorefronts(row).length > 0
    },
    rowMpOwn: function (row) {
      return this.rowMpScope[row.id] != null
    },
    setRowMpMode: function (row, own) {
      if (own) {
        if (!this.rowMpOwn(row)) this.$set(this.rowMpScope, row.id, JSON.parse(JSON.stringify(this.defaultMpScope || {})))
      } else {
        this.$set(this.rowMpScope, row.id, null)
      }
      this.$set(this.mpBoxOpen, row.id, true)
    },
    /** Pill next to the alert name when its marketplaces differ from "everything", so changed rows stand out closed. */
    mpPillVisible: function (row) {
      if (!this.mpBoxVisible(row)) return false
      if (this.rowMpOwn(row)) return true
      return this.mpScopeSummary(this.defaultMpScope, this.mpStorefronts(row)) !== 'All marketplaces'
    },
    mpPillText: function (row) {
      var own = this.rowMpOwn(row)
      return (own ? 'Own · ' : 'Default · ') + this.mpScopeSummary(own ? this.rowMpScope[row.id] : this.defaultMpScope, this.mpStorefronts(row))
    },
    applyDefaultMpScope: function () {
      var k = this.normalizeEmailKey(this.selectedUserEmail)
      var scope = k ? this.notificationScopeByUser[k] : null
      var raw = scope && scope.alertMarketplaceScope ? scope.alertMarketplaceScope : {}
      this.defaultMpScope = JSON.parse(JSON.stringify(raw))
      this.defaultMpScopeSnapshot = JSON.stringify(normalizeMpScope(this.defaultMpScope))
    },
    saveDefaultMpScope: function () {
      var self = this
      if (!self.defaultMpScopeChanged) return Promise.resolve()
      return self.$http.patch(NOTIFICATION_PREFS_MARKETPLACES_URL, {
        user_email: self.selectedUserEmail,
        alert_marketplace_scope: normalizeMpScope(self.defaultMpScope)
      }).then(function (res) {
        var body = (res && res.body) || {}
        var k = self.normalizeEmailKey(self.selectedUserEmail)
        if (k && self.notificationScopeByUser[k]) {
          self.$set(self.notificationScopeByUser[k], 'alertMarketplaceScope', body.alert_marketplace_scope || {})
        }
        self.applyDefaultMpScope()
      })
    },
    serializedBrandAllowlist: function () {
      var src = this.brandAllowlist || {}
      var out = {}
      BRAND_FILTER_ALERTS.forEach(function (alert) {
        var lists = src[alert] || {}
        out[alert] = {}
        Object.keys(lists).sort().forEach(function (k) { out[alert][k] = (lists[k] || []).slice() })
      })
      return JSON.stringify(out)
    },
    brandsFor: function (alert, sellerId) {
      return (this.brandAllowlist[alert] || {})[String(sellerId)] || []
    },
    /** Storefronts the row currently covers — one brand-filter line each. */
    brandStorefronts: function (row) {
      var ids = this.rowSellerIdsFor(row)
      if (!ids.length) return this.assignedStorefronts
      return this.assignedStorefronts.filter(function (sf) { return ids.indexOf(sf.id) >= 0 })
    },
    brandFilterSummary: function (row) {
      var self = this
      var n = this.brandStorefronts(row).filter(function (sf) { return self.brandsFor(row.brandFilter, sf.id).length > 0 }).length
      if (!n) return ''
      return ' · ' + n + ' storefront' + (n === 1 ? '' : 's') + ' filtered'
    },
    brandFilterIsOpen: function (alert) {
      return this.brandFilterOpen[alert] !== false
    },
    setBrandFilterOpen: function (alert, open) {
      if (this.brandFilterIsOpen(alert) !== !!open) this.$set(this.brandFilterOpen, alert, !!open)
    },
    /** Picker key per row (two rows can share one alert's list, e.g. competitor price + Amazon competitive price). */
    brandPickerKeyFor: function (rowId, sellerId) {
      return rowId + '|' + String(sellerId)
    },
    /** GET /api/v1/sellers/brand-allowlists?user_id= — the selected user's own lists on every storefront. */
    loadBrandAllowlists: function () {
      var self = this
      var userId = self.selectedUserIdForApi()
      var params = userId != null && userId !== '' ? { user_id: userId } : {}
      return self.$http.get(USER_BRAND_ALLOWLISTS_URL, { params: params }).then(function (res) {
        var lists = (res.body && res.body.lists) || {}
        var map = emptyBrandAllowlists()
        Object.keys(lists).forEach(function (sid) {
          var perAlert = lists[sid] || {}
          BRAND_FILTER_ALERTS.forEach(function (alert) {
            var list = perAlert[alert]
            map[alert][String(sid)] = Array.isArray(list) ? list.slice() : []
          })
        })
        self.brandAllowlist = map
        self.brandAllowlistSnapshot = self.serializedBrandAllowlist()
        self.brandAllowlistLoaded = true
        // Each alert's section starts collapsed once it has a filter, so the page stays short for set-up accounts.
        var open = {}
        BRAND_FILTER_ALERTS.forEach(function (alert) {
          open[alert] = !Object.keys(map[alert]).some(function (k) { return map[alert][k].length > 0 })
        })
        self.brandFilterOpen = open
      }).catch(function () {
        self.brandAllowlistLoaded = false
      })
    },
    setBrandList: function (alert, sellerId, list) {
      if (!this.brandAllowlist[alert]) this.$set(this.brandAllowlist, alert, {})
      this.$set(this.brandAllowlist[alert], String(sellerId), list)
    },
    removeBrand: function (alert, sellerId, idx) {
      var list = this.brandsFor(alert, sellerId).slice()
      list.splice(idx, 1)
      this.setBrandList(alert, sellerId, list)
    },
    addBrand: function (alert, sellerId, brand) {
      var clean = cleanBrand(brand)
      if (!clean) return
      var list = this.brandsFor(alert, sellerId).slice()
      var exists = list.some(function (b) { return brandKey(b) === brandKey(clean) })
      if (!exists) list.push(clean)
      this.setBrandList(alert, sellerId, list)
      this.brandPickerKey = null
      this.brandQuery = ''
    },
    addTypedBrand: function (alert, sellerId) {
      if (!this.brandQuery.trim()) return
      var q = brandKey(this.brandQuery)
      var match = this.brandPickerOptions(alert, sellerId).filter(function (o) { return brandKey(o.brand) === q })[0]
      this.addBrand(alert, sellerId, match ? match.brand : this.brandQuery)
    },
    toggleBrandPicker: function (alert, sellerId, rowId) {
      var k = this.brandPickerKeyFor(rowId || alert, sellerId)
      this.openStorefrontRowId = null
      if (this.brandPickerKey === k) {
        this.brandPickerKey = null
        return
      }
      this.brandPickerKey = k
      this.brandQuery = ''
      this.fetchBrandOptions(sellerId, '')
      var self = this
      this.$nextTick(function () {
        var el = self.$el && self.$el.querySelector('.als-bf-search')
        if (el) el.focus()
      })
    },
    onBrandQuery: function (sellerId, value) {
      var self = this
      this.brandQuery = value
      if (this.brandQueryTimer) clearTimeout(this.brandQueryTimer)
      this.brandQueryTimer = setTimeout(function () {
        self.fetchBrandOptions(sellerId, value)
      }, 250)
    },
    /** Catalog brands are per storefront (the same for every alert), so the cache is keyed by seller + query. */
    brandOptionsCacheKey: function (sellerId, q) {
      return String(sellerId) + '|' + brandKey(q)
    },
    fetchBrandOptions: function (sellerId, q) {
      var self = this
      var key = this.brandOptionsCacheKey(sellerId, q)
      if (this.brandOptionsCache[key]) return Promise.resolve()
      var query = String(q || '').trim()
      this.brandOptionsLoading = !this.brandOptionsCache[this.brandOptionsCacheKey(sellerId, '')]
      return this.$http.get(sellerBrandsUrl(sellerId), { params: query ? { q: query } : {} }).then(function (res) {
        var list = (res.body && res.body.brands) || []
        self.$set(self.brandOptionsCache, key, list)
        self.brandOptionsLoading = false
      }).catch(function () {
        self.$set(self.brandOptionsCache, key, [])
        self.brandOptionsLoading = false
      })
    },
    /** Picklist for the open storefront: latest server result for the query, minus brands already on this alert's list. */
    brandPickerOptions: function (alert, sellerId) {
      var q = brandKey(this.brandQuery)
      var list = this.brandOptionsCache[this.brandOptionsCacheKey(sellerId, this.brandQuery)]
      if (!list) {
        list = (this.brandOptionsCache[this.brandOptionsCacheKey(sellerId, '')] || []).filter(function (o) {
          return brandKey(o.brand).indexOf(q) >= 0
        })
      }
      var taken = this.brandsFor(alert, sellerId).map(brandKey)
      return list.filter(function (o) { return taken.indexOf(brandKey(o.brand)) < 0 })
    },
    brandQueryIsCustom: function (alert, sellerId) {
      var q = brandKey(this.brandQuery)
      if (!q) return false
      if (this.brandsFor(alert, sellerId).some(function (b) { return brandKey(b) === q })) return false
      return !this.brandPickerOptions(alert, sellerId).some(function (o) { return brandKey(o.brand) === q })
    },
    /** PATCH only the (alert, storefront) lists that changed since load. */
    saveBrandAllowlists: function () {
      var self = this
      if (!self.brandFilterEditable || !self.brandAllowlistChanged) return Promise.resolve()
      var before = emptyBrandAllowlists()
      try { before = JSON.parse(self.brandAllowlistSnapshot) || before } catch (e) {}
      var changed = []
      BRAND_FILTER_ALERTS.forEach(function (alert) {
        var now = self.brandAllowlist[alert] || {}
        var was = before[alert] || {}
        Object.keys(now).forEach(function (k) {
          if (JSON.stringify(now[k] || []) !== JSON.stringify(was[k] || [])) changed.push({ alert: alert, sellerId: k })
        })
      })
      return Promise.all(changed.map(function (c) {
        var userId = self.selectedUserIdForApi()
        var params = userId != null && userId !== '' ? { user_id: userId } : {}
        return self.$http.patch(sellerBrandAllowlistUrl(c.sellerId, c.alert), { brand_allowlist: self.brandsFor(c.alert, c.sellerId) }, { params: params })
          .then(function (res) {
            var saved = res.body && res.body.brand_allowlist
            if (Array.isArray(saved)) self.setBrandList(c.alert, c.sellerId, saved.slice())
          })
      })).then(function () {
        self.brandAllowlistSnapshot = self.serializedBrandAllowlist()
      })
    },
    assignedSellerIdSetForSelectedUser: function () {
      var k = this.normalizeEmailKey(this.selectedUserEmail)
      var scope = k ? this.notificationScopeByUser[k] : null
      var assigned = scope && Array.isArray(scope.assignedSellerIds) ? scope.assignedSellerIds : null
      if (!assigned) {
        var ea = this.selectedEa
        assigned = ea && Array.isArray(ea.allowedSellerAccounts) ? ea.allowedSellerAccounts : []
      }
      return new Set(assigned.map(function (id) { return String(id) }))
    },
    availableSellersForSelectedUser: function () {
      var k = this.normalizeEmailKey(this.selectedUserEmail)
      var scope = k ? this.notificationScopeByUser[k] : null
      if (scope && Array.isArray(scope.availableSellers)) return scope.availableSellers
      return this.sellers
    },
    rowEnabledForPreference: function (row) {
      if (row.alwaysOn) return true
      if (this.rowLocked(row)) return false
      return this.rowOn(row)
    },
    /** IANA timezone (e.g. America/Los_Angeles) for daily/weekly digest schedules. */
    browserIanaTimezoneForApi: function () {
      return browserIanaTimezoneStatic()
    },
    deliveryApiForRow: function (row, hour) {
      var digestTz = { timezone: this.browserIanaTimezoneForApi() }
      if (row.digestFixed) {
        return { frequency: 'daily', schedule: Object.assign({ hour: hour }, digestTz) }
      }
      if (row.alwaysOn) {
        return { frequency: 'real_time', schedule: {} }
      }
      if (this.deliveryRealtimeBadgeOnly(row) || this.rowLocked(row)) {
        return { frequency: 'real_time', schedule: {} }
      }
      var mode = this.deliveryFor(row)
      if (mode === 'realtime') {
        return { frequency: 'real_time', schedule: {} }
      }
      if (mode === 'daily') {
        return { frequency: 'daily', schedule: Object.assign({ hour: hour }, digestTz) }
      }
      if (mode === 'weekly') {
        var sched = Object.assign(
          { hour: hour, weekday: normalizeDeliveryWeekday(this.weeklyDay[row.id]) },
          digestTz
        )
        return { frequency: 'weekly', schedule: sched }
      }
      return { frequency: 'real_time', schedule: {} }
    },
    capturePrefsSnapshot: function () {
      this.prefsSnapshot = this.serializedPrefsForCompare()
    },
    normalizeEmailKey: function (email) {
      return email ? String(email).trim().toLowerCase() : ''
    },
    /** Extract preferences[] from GET / PUT response body (envelope or raw array). */
    preferencesArrayFromSettingsBody: function (body) {
      if (!body) return null
      if (Array.isArray(body)) return body
      if (Array.isArray(body.preferences)) return body.preferences
      if (body.data && Array.isArray(body.data.preferences)) return body.data.preferences
      return null
    },
    /** Parses GET /api/v1/notification-preferences/users (flexible shapes). */
    normalizeNotificationUsersResponse: function (body) {
      var out = {}
      var self = this
      function store (email, prefs) {
        if (!email || !Array.isArray(prefs)) return
        out[self.normalizeEmailKey(email)] = prefs
      }
      if (!body) return out
      if (Array.isArray(body)) {
        body.forEach(function (u) {
          store(u.user_email || u.email || u.userEmail, u.preferences || u.preference_list || u.notification_preferences)
        })
        return out
      }
      if (typeof body === 'object') {
        var rootEmail = body.user_email || body.email || body.userEmail
        var rootPrefs = body.preferences || body.preference_list || body.notification_preferences
        if (rootEmail && Array.isArray(rootPrefs) && body.users == null && !body.preferences_by_user && !body.preferencesByUser) {
          store(rootEmail, rootPrefs)
        }
      }
      var pbu = body.preferences_by_user || body.preferencesByUser || body.by_user
      if (pbu && typeof pbu === 'object' && !Array.isArray(pbu)) {
        Object.keys(pbu).forEach(function (em) {
          var v = pbu[em]
          if (Array.isArray(v)) store(em, v)
          else if (v && Array.isArray(v.preferences)) store(em, v.preferences)
        })
      }
      var users = body.users || body.data || body.results || body.items
      if (Array.isArray(users)) {
        users.forEach(function (u) {
          store(u.user_email || u.email || u.userEmail || u.mail, u.preferences || u.preference_list || u.notification_preferences)
        })
      }
      return out
    },
    normalizeAvailableSellerForUi: function (seller, idx) {
      if (!seller || typeof seller !== 'object') return null
      var sid = seller.sellerId != null ? seller.sellerId : (seller.seller_id != null ? seller.seller_id : (seller.id != null ? seller.id : null))
      if (sid != null && typeof sid === 'object' && sid.value !== undefined) sid = sid.value
      if (sid == null || sid === '') return null
      var name = seller.nickName || seller.nick_name || seller.seller_name || seller.store_name || seller.name || ('Storefront ' + String(sid).slice(-6))
      var mpCodes = null
      if (Array.isArray(seller.marketplace_codes) && seller.marketplace_codes.length) {
        mpCodes = seller.marketplace_codes.map(function (c) { return String(c).toLowerCase() })
      } else if (Array.isArray(seller.marketplaceCodes) && seller.marketplaceCodes.length) {
        mpCodes = seller.marketplaceCodes.map(function (c) { return String(c).toLowerCase() })
      }
      return {
        sellerId: sid,
        nickName: name,
        marketplaceIso31662Code: seller.marketplaceIso31662Code || seller.marketplace_iso31662_code || seller.marketplace || (mpCodes && mpCodes[0]) || '',
        marketplaceCodes: mpCodes,
        region: seller.region || '',
        key: String(sid) + '-' + String(idx)
      }
    },
    notificationScopeFromBody: function (body) {
      if (!body || typeof body !== 'object') return null
      var root = body.data && typeof body.data === 'object' ? body.data : body
      var assignedSellers = root.assigned_sellers || root.assignedSellers
      var assignedIds = root.assigned_seller_ids || root.assignedSellerIds
      var available = root.available_sellers || root.availableSellers
      if (!Array.isArray(available) && Array.isArray(root.preferences)) {
        var seenAvail = {}
        available = []
        root.preferences.forEach(function (pref) {
          if (!Array.isArray(pref.available_sellers)) return
          pref.available_sellers.forEach(function (s) {
            var sid = s.id != null ? s.id : (s.seller_id != null ? s.seller_id : null)
            var key = sid != null ? String(sid) : (s.name || '')
            if (!key || seenAvail[key]) return
            seenAvail[key] = true
            available.push(s)
          })
        })
        if (!available.length) available = null
      }
      if (!Array.isArray(assignedSellers) && !Array.isArray(assignedIds) && !Array.isArray(available)) return null
      var normalizedAssigned
      if (Array.isArray(assignedSellers)) {
        normalizedAssigned = assignedSellers.map(function (item) {
          if (!item || typeof item !== 'object') return null
          var id = item.seller_id != null ? item.seller_id : (item.sellerId != null ? item.sellerId : null)
          if (id != null && typeof id === 'object' && id.value !== undefined) id = id.value
          return id != null && id !== '' ? id : null
        }).filter(function (id) { return id != null })
      } else {
        normalizedAssigned = Array.isArray(assignedIds) ? assignedIds.map(function (id) {
          if (id != null && typeof id === 'object' && id.value !== undefined) return id.value
          return id
        }).filter(function (id) { return id != null && id !== '' }) : []
      }
      var normalizedAvailable = Array.isArray(available)
        ? available.map(this.normalizeAvailableSellerForUi).filter(function (s) { return s != null })
        : []
      var mpScope = root.alert_marketplace_scope || root.alertMarketplaceScope
      return {
        assignedSellerIds: normalizedAssigned,
        availableSellers: normalizedAvailable,
        alertMarketplaceScope: mpScope && typeof mpScope === 'object' ? mpScope : {}
      }
    },
    hydrateNotificationScopeUsersResponse: function (body) {
      var self = this
      if (!body || typeof body !== 'object') return
      var users = Array.isArray(body) ? body : (body.users || body.data || body.results || body.items)
      if (!Array.isArray(users)) return
      users.forEach(function (u) {
        if (!u || typeof u !== 'object') return
        var em = u.user_email || u.email || u.userEmail || u.mail
        var k = self.normalizeEmailKey(em)
        if (!k) return
        var scope = self.notificationScopeFromBody(u)
        if (!scope) return
        self.$set(self.notificationScopeByUser, k, scope)
      })
    },
    fetchNotificationPreferencesUsersBulk: function () {
      var self = this
      return self.$http.get(NOTIFICATION_PREFS_USERS_URL).then(function (res) {
        var body = res.body || {}
        self.notificationPrefsByUser = self.normalizeNotificationUsersResponse(body)
        self.hydrateNotificationScopeUsersResponse(body)
        self.notificationPrefsBulkLoaded = true
      }).catch(function () {
        self.notificationPrefsBulkLoaded = false
      })
    },
    /** When bulk succeeded, prefs for email are present in notificationPrefsByUser. Empty array is treated as cache miss (fall back to GET). */
    prefsFromBulkCache: function (email) {
      var k = this.normalizeEmailKey(email)
      if (!this.notificationPrefsBulkLoaded || !k) return undefined
      if (!Object.prototype.hasOwnProperty.call(this.notificationPrefsByUser, k)) return undefined
      var prefs = this.notificationPrefsByUser[k]
      if (Array.isArray(prefs) && prefs.length === 0) return undefined
      return prefs
    },
    /**
     * Load prefs for `selectedUserEmail`.
     * @param {{ forceUserGet?: boolean }} [opts] — if `forceUserGet`, always GET /api/v1/notification-preferences?user_email=… (e.g. dropdown change); otherwise use bulk cache when available.
     */
    loadNotificationPreferencesFromApi: function (opts) {
      var self = this
      var force = opts && opts.forceUserGet === true
      if (!self.selectedUserEmail) return Promise.resolve()
      if (!force) {
        var cached = self.prefsFromBulkCache(self.selectedUserEmail)
        if (cached !== undefined) {
          self.applyNotificationPreferences(Array.isArray(cached) ? cached : [])
          self.applyDefaultMpScope()
          return Promise.resolve()
        }
      }
      return self.$http.get(NOTIFICATION_PREFS_URL, { params: { user_email: self.selectedUserEmail } }).then(function (res) {
        var body = res.body || {}
        var scope = self.notificationScopeFromBody(body)
        var k = self.normalizeEmailKey(self.selectedUserEmail)
        if (k && scope) self.$set(self.notificationScopeByUser, k, scope)
        var prefs = self.preferencesArrayFromSettingsBody(body)
        if (Array.isArray(prefs)) self.applyNotificationPreferences(prefs)
        self.applyDefaultMpScope()
        self.applyNotificationLocaleFromBody(body)
      })
    },
    applyNotificationLocaleFromBody: function (body) {
      var locale = body.notification_locale || body.notificationLocale || 'en'
      this.notificationLocale = String(locale).toLowerCase()
      var supported = body.supported_notification_locales || body.supportedNotificationLocales
      if (Array.isArray(supported) && supported.length) {
        this.supportedNotificationLocales = supported.map(function (c) { return String(c).toLowerCase() })
      }
    },
    saveNotificationLocale: function () {
      var self = this
      if (!self.selectedUserEmail) return
      self.localeSaving = true
      self.localeError = null
      self.localeSaved = false
      self.$http.patch(NOTIFICATION_PREFS_LOCALE_URL, {
        user_email: self.selectedUserEmail,
        notification_locale: self.notificationLocale
      }).then(function () {
        self.localeSaving = false
        self.localeSaved = true
        setTimeout(function () { self.localeSaved = false }, 2500)
      }, function (err) {
        self.localeSaving = false
        var body = err.body || {}
        self.localeError = body.message || body.error || 'Could not save notification language.'
      })
    },
    applyNotificationPreferences: function (prefs) {
      var self = this
      var ea = this.selectedEa
      if (!ea || !Array.isArray(prefs) || prefs.length === 0) return
      prefs.forEach(function (p) {
        var row = rowForAlertCategory(p.alert_category)
        if (!row) return
        self.$set(self.rowSellerIds, row.id, numericIdList(p.seller_ids))
        var ownMp = p.marketplace_scope && typeof p.marketplace_scope === 'object' ? JSON.parse(JSON.stringify(p.marketplace_scope)) : null
        self.$set(self.rowMpScope, row.id, ownMp)
        if (ownMp && self.mpBoxOpen[row.id] === undefined) self.$set(self.mpBoxOpen, row.id, true)
        if (row.subtypes) {
          self.$set(self.rowMutedSubtypes, row.id, Array.isArray(p.muted_subtypes) ? p.muted_subtypes.slice() : [])
        }
        if (row.settingsKey) {
          self.$set(self.rowSettings, row.id, normalizeRowSettings(row.settingsKey, p.settings))
        }
        var en = p.enabled === true
        if (row.alwaysOn && !row.digestFixed) {
          /* remaining always-on realtime rows */
        } else if (row.digestFixed) {
          if (row.localKey) self.$set(self.localRowOn, row.id, en)
          if (p.delivery_schedule && p.delivery_schedule.hour != null) {
            var dhRaw = Number(p.delivery_schedule.hour)
            var dsDigest = p.delivery_schedule || {}
            var dh = deliveryScheduleHourToBrowserLocalHour(dhRaw, dsDigest, 'daily', null)
            self.$set(self.rowDigestHour, row.id, dh)
            self.$set(ea, 'digestEmailHour', dh)
            try {
              window.localStorage.setItem('als_hour|' + self.hydrationKey(row.id), String(dh))
            } catch (eH) {}
          }
        } else if (row.localKey) {
          self.$set(self.localRowOn, row.id, en)
        } else if (row.wdt) {
          var wd = self.ensureWd(ea, row.wdt)
          if (!wd.orig) wd.orig = { RED: wd.RED, AMBER: wd.AMBER, GREEN: wd.GREEN }
          wd.RED = wd.AMBER = wd.GREEN = en
        }
        var freq = String(p.delivery_frequency || 'real_time').toLowerCase()
        var uiDel = freq === 'real_time' ? 'realtime' : freq
        if (!self.deliveryRealtimeBadgeOnly(row) && !row.alwaysOn && !row.digestFixed && !self.rowLocked(row)) {
          var coerced = uiDel
          self.$set(self.rowDelivery, row.id, coerced)
          try {
            window.localStorage.setItem(self.lsPrefix + self.hydrationKey(row.id), coerced)
          } catch (e) {}
        }
        var ds = p.delivery_schedule || {}
        if (ds.hour != null && ea && !row.digestFixed && (freq === 'daily' || freq === 'weekly')) {
          var rowHRaw = Number(ds.hour)
          var rowH = deliveryScheduleHourToBrowserLocalHour(rowHRaw, ds, freq, ds.weekday)
          self.$set(self.rowDigestHour, row.id, rowH)
          try {
            window.localStorage.setItem('als_hour|' + self.hydrationKey(row.id), String(rowH))
          } catch (eHr) {}
        }
        if (ds.weekday != null && row.id && freq === 'weekly') {
          var wdayCanonical = normalizeDeliveryWeekday(ds.weekday)
          self.$set(self.weeklyDay, row.id, wdayCanonical)
          try {
            window.localStorage.setItem('als_wday|' + self.hydrationKey(row.id), wdayCanonical)
          } catch (e2) {}
        }
        if (row.delayMinutesConfigurable) {
          self.applyBuyboxDelayState(row, normalizeBuyboxDelayMinutes(p.delay_minutes))
        }
      })
      try {
        self.persistLocalRowOnJson()
      } catch (e3) {}
    },
    onInboxModeChange: function (threaded) {
      var self = this
      var prev = this.threadedAlerts
      if (prev === threaded) return
      this.threadedAlerts = threaded
      this.inboxModeSaving = true
      this.$http.put('/api/identity/settings', { threadedAlerts: threaded }).then(function (res) {
        self.inboxModeSaving = false
        var body = (res && res.body) || {}
        if (body.threadedAlerts != null) self.threadedAlerts = body.threadedAlerts === true
        self.successMsg = 'Inbox mode: ' + (self.threadedAlerts ? 'Threaded' : 'Separate emails') + '.'
        setTimeout(function () { self.successMsg = '' }, 2800)
      }, function (err) {
        self.inboxModeSaving = false
        self.threadedAlerts = prev
        var msg = err && err.body && err.body.message
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ')
        self.errorMsg = msg || 'Failed to save inbox mode'
        setTimeout(function () { self.errorMsg = '' }, 4500)
      })
    },
    onDigestToggle: function (on) {
      var self = this
      var next = !!on
      var prev = this.offerActivityDigest
      this.offerActivityDigest = next
      this.digestSaving = true
      this.$http.put('/api/identity/settings', { offerActivityDigest: next }).then(function (res) {
        self.digestSaving = false
        var body = (res && res.body) || {}
        var v = body.offerActivityDigest
        if (v == null) v = body.offer_activity_digest
        if (v != null) {
          self.offerActivityDigest = v === true || v === 1 || v === '1' || v === 'true'
        }
        self.successMsg = 'Offer activity digest ' + (self.offerActivityDigest ? 'on' : 'off') + '.'
        setTimeout(function () { self.successMsg = '' }, 2800)
      }, function (err) {
        self.digestSaving = false
        self.offerActivityDigest = prev
        var msg = err && err.body && err.body.message
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ')
        self.errorMsg = msg || 'Failed to save digest setting'
        setTimeout(function () { self.errorMsg = '' }, 4500)
      })
    },
    save: function () {
      var self = this
      if (!self.hasChanges) return
      if (self.hasDelayValidationError) {
        self.errorMsg = 'Fix the Buy Box delay: enter a whole number between 20 and 40 minutes.'
        setTimeout(function () { self.errorMsg = '' }, 4500)
        return
      }
      var userId = self.selectedUserIdForApi()
      if (userId == null || userId === '') {
        self.errorMsg = 'Cannot save: missing user id for this member.'
        setTimeout(function () { self.errorMsg = '' }, 4500)
        return
      }
      self.saving = true
      var body = { preferences: self.buildPreferencesArray() }
      var params = { user_email: self.selectedUserEmail, user_id: userId }
      // Default marketplaces first: the alert rows that follow it are saved right after.
      self.saveDefaultMpScope().then(function () {
        return self.$http.put(NOTIFICATION_PREFS_URL, body, { params: params })
      }).then(function () {
        return self.saveBrandAllowlists()
      }).then(function () {
        self.persistLocalRowOnJson()
        if (self.isOwner) {
          return self.fetchNotificationPreferencesUsersBulk().catch(function () {})
        }
      }).then(function () {
        return self.loadNotificationPreferencesFromApi({ forceUserGet: true })
      }).then(function () {
        self.capturePrefsSnapshot()
        window.dirty = false
        self.saving = false
        self.successMsg = 'Preferences saved.'
        setTimeout(function () { self.successMsg = '' }, 2800)
      }).catch(function (err) {
        self.saving = false
        self.errorMsg = (err.body && err.body.message) || 'Save failed'
        setTimeout(function () { self.errorMsg = '' }, 4500)
      })
    }
  }
}

</script>

<style scoped>
.als {
  font-size: 14px;
  color: var(--text-primary);
}

.als-loading { display:flex; justify-content:center; align-items:center; min-height: 280px; }
.als-spinner { width:36px; height:36px; border:3px solid var(--border-light); border-top-color: var(--primary-navy); border-radius:50%; animation: als-spin 0.7s linear infinite; }
@keyframes als-spin { to { transform: rotate(360deg); } }

.als-error { text-align:center; padding: 48px 24px; }
.als-error-title { font-weight:600; margin: 0 0 8px; }
.als-error-msg { color: var(--text-secondary); margin-bottom:16px; }

.als-page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.als-page-head-text { min-width: 0; flex: 1; }
.als-title { margin: 0 0 6px; font-size: 26px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.01em; }
.als-subtitle { margin: 0; color: var(--text-secondary); line-height: 1.5; max-width: 640px; font-size: 14px; }

.als-coverage-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  background: var(--accent-orange-light, #fef3e8);
  border: 1px solid rgba(245, 194, 138, 0.85);
  color: #8a4a10;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 999px;
  white-space: nowrap;
}
.als-coverage-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent-orange, #f5841f);
}

.als-hint-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid var(--border-light);
  background: linear-gradient(180deg, #fafcff 0%, #f2f6fb 100%);
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--text-primary);
}
.als-hint-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--accent-orange, #f5841f);
}
.als-hint-body strong { font-weight: 600; }

.als-config-card {
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card, 8px);
  padding: 20px;
  margin-bottom: 28px;
  background: var(--bg-white);
}
.als-config-top { display:flex; justify-content: space-between; align-items: flex-start; gap:16px; margin-bottom: 16px; }
.als-digest-card .als-config-top { margin-bottom: 0; align-items: center; }
.als-toggle-busy { opacity: 0.6; pointer-events: none; }
.als-inbox-options { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.als-inbox-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 15px;
  border: 1px solid var(--border-medium);
  border-radius: 8px;
  cursor: pointer;
}
.als-inbox-option-sel { border-color: var(--primary-navy); box-shadow: inset 0 0 0 1px var(--primary-navy); background: var(--bg-page, #f6f8fb); }
.als-inbox-option input { margin-top: 3px; width: 16px; height: 16px; flex-shrink: 0; }
.als-inbox-option-text { display: flex; flex-direction: column; gap: 4px; }
.als-inbox-option-text strong { font-size: 15px; font-weight: 600; }
.als-inbox-option-text span { font-size: 13px; color: var(--text-secondary); }
@media (max-width: 640px) {
  .als-inbox-options { grid-template-columns: 1fr; }
}
.als-config-title { margin:0 0 4px; font-size: 16px; font-weight: 600; }
.als-config-hint { margin: 0; color: var(--text-secondary); font-size: 13px; }
.als-user-select {
  min-width: 220px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-medium);
  background: var(--bg-white);
}
.als-brands { display: flex; flex-direction: column; gap:10px; }
.als-brand-row { display:flex; flex-wrap:wrap; align-items: center; gap:10px; }
.als-brand-name { font-weight: 600; min-width: 120px; }
.als-mp-tags { display:flex; flex-wrap:wrap; gap:6px; }
.als-mp-tag {
  display:inline-flex;
  align-items:center;
  gap:4px;
  padding:3px 8px;
  background: var(--bg-page, #f6f8fb);
  border-radius:999px;
  font-size:12px;
  color: var(--text-secondary);
}
.als-mp-tag i.flag { margin:0!important; }

/* Alert marketplaces: default row on the config card + per-alert box */
.als-mp-default-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; }
.als-mp-default-title { font-weight: 700; font-size: 13px; color: var(--primary-navy); }
.als-mp-default-hint { font-size: 12.5px; color: var(--text-secondary); }
.als-mp-chips { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.als-mp-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 3px 10px;
  border: 1px solid #d8dee8;
  border-radius: 999px;
  background: #fff;
  color: #4a5a6e;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.als-mp-chip i.flag { margin: 0!important; }
.als-mp-chip-all { border-style: dashed; }
.als-mp-chip:hover:not(.active):not(:disabled) { background: #f8fafc; color: var(--primary-navy); }
.als-mp-chip.active { background: var(--primary-navy); border-color: var(--primary-navy); border-style: solid; color: #fff; }
.als-mp-chip:disabled { cursor: default; opacity: .6; }
.als-mp-chip:focus-visible { outline: 2px solid var(--primary-navy); outline-offset: 2px; }
.als-mp-warn { flex-basis: 100%; margin: 0; color: #b54708; font-size: 12px; font-weight: 600; }
.als-mp-mode { margin-bottom: 4px; }
.als-mp-mode .als-seg-btn { padding: 6px 12px; font-size: 12.5px; }
.als-mp-follow { margin: 8px 0 0; font-size: 12.5px; color: var(--text-secondary); }
.als-mp-follow b { color: var(--text-primary); }
.als-mp-follow-row { margin: -8px 24px 14px; }
.als-mp-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2f7;
  color: #4a5a6e;
  font-size: 11px;
  font-weight: 600;
  vertical-align: 2px;
}
.als-mp-pill-own { background: #fff7ed; color: #b45309; border: 1px solid #fed7aa; }

/* "Alert me about": sub-types inside one alert */
.als-st-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 6px 16px; margin: 10px 0 4px; }
.als-st-opt { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 13px; font-weight: 400; color: var(--text-primary); cursor: pointer; }
.als-st-opt input { margin: 0; accent-color: var(--primary-navy); }
.als-st-reset { margin-right: 6px; padding: 0; border: 0; background: none; color: var(--primary-navy); font-weight: 600; font-size: 12px; cursor: pointer; }
.als-st-reset:hover { text-decoration: underline; }
.als-settings-label { margin: 10px 0 0; }
.als-settings-line { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding-top: 10px; font-size: 13px; }
.als-settings-label, .als-settings-line .als-subrow-label { font-weight: 700; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-tertiary, #8794a8); }
.als-muted-sm { margin: 0; color: var(--text-secondary); font-size: 13px; }
.als-config-footer { margin: 16px 0 0; font-size: 13px; color: var(--text-secondary); }
.als-link { color: var(--primary-navy); font-weight: 600; text-decoration:none; }
.als-link:hover { text-decoration: underline; }

.als-panel {
  border: 1px solid var(--border-light);
  border-radius: 12px;
  /* visible so the Storefronts / brand dropdowns are not clipped; corners handled below */
  overflow: visible;
  background: var(--bg-white);
  box-shadow: 0 1px 2px rgba(18, 46, 82, 0.04);
}
.als-cat {
  padding: 12px 24px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: #eef3f9;
  color: var(--primary-navy);
  border-top: 1px solid var(--border-light);
  border-bottom: 1px solid var(--border-light);
}
.als-panel > div:first-child .als-cat { border-top: 0; border-radius: 12px 12px 0 0; }
.als-panel > div:last-child .als-row-wrap:last-child,
.als-panel > div:last-child .als-row-wrap:last-child > :last-child { border-radius: 0 0 12px 12px; }

/* Storefronts chip (per-alert storefront scope) */
.als-sf-chip { position: relative; margin-right: 12px; }
.als-sf-chip-btn {
  border: 1px solid var(--border-medium);
  background: var(--bg-white);
  color: var(--primary-navy);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.als-sf-chip-btn:hover { border-color: var(--primary-navy); }
.als-sf-caret { font-size: 10px; margin-left: 2px; }
.als-sf-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 30;
  width: 360px;
  max-width: calc(100vw - 32px);
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(18, 46, 82, 0.15);
  padding: 8px;
  text-align: left;
}
.als-sf-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-primary);
}
.als-sf-opt:hover { background: var(--bg-page, #f6f8fb); }
.als-sf-list { padding-left: 20px; }
.als-sf-name { font-weight: 600; white-space: nowrap; flex-shrink: 0; }
.als-sf-flags { display: inline-flex; flex-wrap: wrap; justify-content: flex-end; gap: 4px; margin-left: auto; min-width: 0; }
.als-sf-flags i.flag { margin: 0 !important; }
.als-sf-err { margin: 2px 6px 4px; color: #b42318; font-size: 12px; font-weight: 600; }
.als-sf-note {
  margin: 6px 0 0;
  padding: 6px 6px 2px;
  border-top: 1px solid var(--border-light);
  font-size: 12px;
  color: var(--text-tertiary, #8794a8);
}
.als-sf-warn {
  margin: 0;
  max-width: 320px;
  text-align: right;
  color: #b54708;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

/* Hijacker brand filter */
.als-bf {
  margin: 0 24px 16px;
  padding: 12px 14px;
  border: 1px solid var(--border-light);
  border-radius: 10px;
  background: #fafbfd;
}
.als-bf-head { cursor: pointer; font-weight: 600; color: var(--primary-navy); list-style: none; font-size: 13.5px; }
.als-bf-head::-webkit-details-marker { display: none; }
.als-bf-caret { display: inline-block; margin-right: 6px; transition: transform .15s ease; }
.als-bf[open] .als-bf-caret { transform: rotate(90deg); }
.als-bf-sub { font-weight: 400; color: var(--text-secondary); font-size: 12.5px; }
.als-bf-hint { margin: 6px 0 10px; font-size: 12.5px; color: var(--text-secondary); }
.als-bf-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--border-light);
}
.als-bf-name { font-weight: 600; min-width: 120px; font-size: 13px; }
.als-bf-all { color: var(--text-tertiary, #8794a8); font-size: 12.5px; font-style: italic; }
.als-bf-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px 3px 10px;
  border-radius: 999px;
  background: var(--primary-navy);
  color: #fff;
  font-size: 12px;
}
.als-bf-x { background: none; border: 0; color: #fff; cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }
.als-bf-add { position: relative; }
.als-bf-add-btn {
  border: 1px dashed var(--primary-navy);
  background: var(--bg-white);
  color: var(--primary-navy);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
}
.als-bf-pick {
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 30;
  width: 280px;
  max-height: 320px;
  overflow-y: auto;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(18, 46, 82, 0.15);
  padding: 8px;
}
.als-bf-search {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  margin-bottom: 6px;
  border: 1px solid var(--border-medium);
  border-radius: 6px;
  font-size: 12.5px;
}
.als-bf-opt {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px;
  border: 0;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
  color: var(--text-primary);
}
.als-bf-opt:hover { background: var(--bg-page, #f6f8fb); }
.als-bf-opt small { color: var(--text-tertiary, #8794a8); white-space: nowrap; }
.als-bf-custom { color: var(--primary-navy); font-weight: 600; }
.als-bf-empty { margin: 4px 6px; font-size: 12px; color: var(--text-tertiary, #8794a8); }
.als-bf-ro { margin: 8px 0 0; font-size: 12px; color: var(--text-tertiary, #8794a8); }

@media (max-width: 760px) {
  .als-sf-menu { right: auto; left: 0; }
  .als-bf { margin: 0 12px 12px; }
  .als-bf-name { min-width: 0; width: 100%; }
}

.als-row-wrap { border-bottom: 1px solid var(--border-light); }
.als-row-wrap:last-child { border-bottom: 0; }
.als-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 24px;
}
.als-row-main { flex: 1; min-width: 0; padding-top: 2px; }
.als-row-controls {
  flex-shrink: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
/* Toggle immediately before (left of) frequency segment — same row, vertically centered */
.als-row-controls-stack {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.als-row-controls-top {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-height: 28px;
}
.als-row-controls-bottom {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
}
.als-delay-inline {
  gap: 8px;
}
.als-delay-unit {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #8794a8);
}
.als-delay-error-inline {
  margin: 0;
  max-width: 280px;
  text-align: right;
  color: #b42318;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.als-alert-title { font-size: 14.5px; font-weight: 700; color: var(--primary-navy); margin-bottom: 4px; }
.als-alert-desc { font-size: 13px; color: var(--text-secondary); line-height: 1.5; max-width: 640px; }
.als-includes-label { font-weight: 600; color: var(--text-primary); }
.als-note {
  font-size: 12px;
  color: var(--text-tertiary, #8794a8);
  font-style: italic;
  display: inline;
}
.als-row-locked { opacity: 0.95; }
.als-starter-lock {
  display:inline-flex; align-items:center; gap:4px;
  font-size: 12px; font-weight: 600;
  color: var(--text-secondary);
}
.als-starter-lock--toggle {
  margin-left: 0;
  white-space: nowrap;
}

.als-pill-on {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #e7f7ec;
  color: #16a34a;
}
.als-pill-on::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #16a34a;
}

.als-toggle {
  position: relative;
  display: inline-flex;
  width: 48px;
  height: 26px;
  cursor: pointer;
  flex-shrink: 0;
}
.als-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.als-toggle-slider {
  border-radius: 999px;
  background: #c9d1dd;
  width: 48px;
  height: 26px;
  transition: background 0.15s ease;
  position: relative;
}
.als-toggle-slider:after {
  content: '';
  position: absolute;
  left: 3px;
  top: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  transition: transform 0.18s ease;
}
.als-toggle input:checked + .als-toggle-slider:after {
  transform: translateX(22px);
}
.als-toggle input:checked + .als-toggle-slider {
  background: var(--primary-navy);
}

.als-seg {
  display: inline-flex;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-white);
}
.als-seg-btn {
  border: 0;
  border-right: 1px solid #d8dee8;
  padding: 8px 14px;
  border-radius: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #fff;
  color: #4a5a6e;
  margin: 0;
  min-width: 0;
  white-space: nowrap;
}
.als-seg-btn:last-child { border-right: 0; }
.als-seg-btn:hover:not(.active) {
  background: #f8fafc;
  color: var(--primary-navy);
}
.als-seg-btn.active {
  background: var(--primary-navy);
  color: #fff;
}

.als-badge-freq {
  display: inline-block;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid var(--border-medium);
  background: var(--bg-white);
  color: var(--text-secondary);
}

.als-subrow {
  padding: 12px 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  background: #fafbfd;
  border-bottom: 1px solid var(--border-light);
}
.als-subrow-label {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #8794a8);
}
.als-subrow-hint { color: var(--text-tertiary, #8794a8); font-size: 12.5px; }
.als-time-select {
  padding:6px 8px;
  border-radius:6px;border:1px solid var(--border-medium);
}
.als-delay-input {
  width: 72px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid var(--border-medium);
  background: var(--bg-white);
  font-size: 13px;
  color: var(--text-primary);
  text-align: center;
}
.als-delay-input:focus {
  outline: none;
  border-color: var(--primary-navy);
}
.als-delay-input-err {
  border-color: #c23b3b;
  background: #fff7f7;
}
.als-delay-input-err:focus {
  border-color: #c23b3b;
}

.als-footer-bar {
  padding: 20px 0 8px;
  display:flex;
  justify-content:flex-end;
  align-items:center;
}

.als-btn {
  border-radius:var(--radius-btn, 6px);
  padding: 10px 22px;font-size:14px;font-weight:600;
  cursor:pointer;border:none;
}
.als-btn-outline { background:white;border:1px solid var(--border-medium);}
.als-btn-primary { background: var(--primary-navy);color:#fff; }
.als-btn-primary:disabled { opacity:.45; cursor:not-allowed; }
.als-btn-loading { opacity: .72; pointer-events:none; }

.als-toast {
  position: fixed; bottom:28px; right:28px; z-index: 10002;
  padding:12px 18px;font-size:14px;border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,.08);
}
.als-toast-ok { background:#eaf7ef;color:#226243; font-weight:600; }
.als-toast-err { background:#fdeaea;color:#9b2c2c; font-weight:600; }

.als-slide-up-enter-active, .als-slide-up-leave-active { transition: opacity .22s ease, transform .22s ease; }
.als-slide-up-enter, .als-slide-up-leave-to { opacity:0; transform: translateY(10px);}
</style>



// WEBPACK FOOTER //
// AlertSettings.vue?6a0634a1